import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isSelectable, findHint, shuffleRemaining, computeFreeSet, reseedSolvable } from "../lib/mahjong";
import { TileFace, TILE_META } from "../components/TileFace";
import { sfx } from "../lib/sound";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  RotateCcw, Lightbulb, Shuffle, Home, Clock, Coins, Flame, Heart, PlayCircle, XCircle, Volume2, VolumeX, Sparkles, LayoutGrid, Pause, Play as PlayIcon, Footprints, Share2, Gift, TrendingUp, Trophy,
} from "lucide-react";
import MockAdDialog from "../components/MockAdDialog";
import WinCelebration from "../components/WinCelebration";
import FirstMatchCelebration from "../components/FirstMatchCelebration";
import WorldUnlockCelebration from "../components/WorldUnlockCelebration";
import EnduranceBestCelebration from "../components/EnduranceBestCelebration";
import PartnerPerkPopup, { partnerPerkOptedOut } from "../components/PartnerPerkPopup";
import { partnerPerksEnabled } from "../lib/rewards";
import CountdownOverlay from "../components/CountdownOverlay";
import { playCoachWin, playCoachCombo, playCoachTimeUp } from "../lib/coach";
import { isNative, showInterstitial, showRewarded, preloadRewarded, adDiag } from "../lib/admob";
import { isHealthSupported, autoSyncStepsIfDue } from "../lib/health";
import { isMuted, setMuted } from "../lib/sound";
import { shareResult, buildShareText } from "../lib/share";
import { translateLevelName, translateWorldTitle } from "../lib/journeyLabels";
import { maybeRequestReview } from "../lib/review";
import { buildLayout } from "../lib/boardGen";
import { enqueueOp, flushQueue, resolveOfflineBoard, cacheModeBoard, isOnline } from "../lib/offlineQueue";
import { useTranslation } from "react-i18next";

// Iter56 — STANDARDIZED PORTRAIT TILES.
// Every layout is now constrained to 7 cols × 10 rows max so the tile
// scale is identical across every board on every Android phone. Tile
// proportions match industry-standard mahjong tiles (~1.30 aspect).
// Tester request: "I dont want pan/scroll - layout must fit every time.
// If necessary replace layouts of any existing boards if they wont fit
// after standardising the tiles."
const TILE_W = 80;
const TILE_H = 104;
const LAYER_OFFSET_X = 8;
const LAYER_OFFSET_Y = -10;
// Iter64 — width-binding reference. REF_COLS=7, REF_ROWS=7 made width
// the binding scale constraint and let tiles render at ~44-50 px wide.
//
// Iter75 — Tester verdict (after dropping `barbell`/`dragon`/`turtle`):
// "board size and tile size should be CONSISTENT across the game".
// REF_ROWS was bumped 7 → 10 so the uniform scale calculation now
// accommodates the tallest layout (gym_temple / gym_fortress / obelisk
// are all 10 rows). Result: every non-outlier board renders at the
// SAME tile size (~44 px on a typical phone) — Daily Workout no
// longer feels smaller than journey levels. The `actualFit` clamp
// still guards the few wider-than-7-col layouts (pullup_bar, track,
// butterfly, fish) from clipping.
const REF_COLS = 7;
// Iter129 — the six 10-row layouts (gym_temple/fortress/obelisk/flame/
// hourglass/dumbbell_tower) were trimmed to 9 rows so EVERY in-use layout
// now fits in ≤9 rows. REF_ROWS bumped 10 → 9 so the uniform scale packs
// tiles slightly larger while still fitting the tallest board.
const REF_ROWS = 9;
// Hard floor — tiles can NEVER render below this many CSS pixels wide.
// Below ~44 px touch targets shrink under Material Design's 48 dp guideline
// and tapping precision tanks on small Android devices.
const MIN_TILE_PX = 44;

// Board theme palettes — wired to profile.selected_theme. `page` gradient
// fills the entire Play screen so the theme truly covers the whole page;
// `board` is the darker inner board surface that keeps the playfield readable.
const BOARD_THEMES = {
  zen_garden: {
    label: "Zen Garden",
    page: "linear-gradient(180deg, #2a3330 0%, #1a1f1d 55%, #0f1412 100%)",
    board: "radial-gradient(ellipse at top, #2a3330 0%, #1a1f1d 60%, #0f1412 100%)",
    photo: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1600&q=60",
    accent: "#E8A365",
  },
  nature_trail: {
    label: "Nature Trail",
    page: "linear-gradient(180deg, #3a4c37 0%, #2a3a28 55%, #162013 100%)",
    board: "radial-gradient(ellipse at top, #3a4c37 0%, #2a3a28 55%, #162013 100%)",
    photo: "https://images.unsplash.com/photo-1434682881908-b43d0467b798?auto=format&fit=crop&w=1600&q=60",
    accent: "#CDE079",
  },
  yoga_studio: {
    label: "Yoga Studio",
    page: "linear-gradient(180deg, #4a4237 0%, #2f2a23 55%, #1a1611 100%)",
    board: "radial-gradient(ellipse at top, #4a4237 0%, #2f2a23 55%, #1a1611 100%)",
    photo: "https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&w=1600&q=60",
    accent: "#F6C3A4",
  },
};

export default function Play() {
  const { t } = useTranslation();
  const { worldId, levelId } = useParams();
  const navigate = useNavigate();
  const { profile, setProfile, refresh } = useAuth();
  const [session, setSession] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hint, setHint] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [boostersUsed, setBoostersUsed] = useState({ hint: 0, shuffle: 0, undo: 0 });
  const [history, setHistory] = useState([]);
  const [endDialog, setEndDialog] = useState(null); // {won, coins, calories}
  const [interstitial, setInterstitial] = useState(false);
  const [interstitialSecs, setInterstitialSecs] = useState(4);
  const [continueAd, setContinueAd] = useState(false);
  const [noMoves, setNoMoves] = useState(false);
  const [shuffleOutOfStock, setShuffleOutOfStock] = useState(false);
  const [shuffleAdOpen, setShuffleAdOpen] = useState(false);
  const [hintOutOfStock, setHintOutOfStock] = useState(false);
  const [hintAdOpen, setHintAdOpen] = useState(false);
  // True while a rewarded ad is loading/playing. Pauses the game timer so
  // the player doesn't lose seconds to ad load time (tester complaint).
  // Iter78 — Mirror to a ref so the running setInterval callback can
  // bail out synchronously even before React re-renders the timer
  // effect (belt-and-braces against a stale state read).
  const [adPaused, _setAdPaused] = useState(false);
  const adPausedRef = useRef(false);
  const setAdPaused = useCallback((v) => {
    const next = typeof v === "function" ? v(adPausedRef.current) : v;
    adPausedRef.current = !!next;
    _setAdPaused(!!next);
    // Visible in `adb logcat | grep Fitjong` so we can prove from the
    // device that the pause flag flipped at the right moment.
    console.log(`[Fitjong][timer] adPaused -> ${next}`);
  }, []);
  const [undoOutOfStock, setUndoOutOfStock] = useState(false);
  const [undoAdOpen, setUndoAdOpen] = useState(false);
  // True while an actual rewarded AD is on screen (any booster ad OR the
  // time-up "continue" ad). Used to pause the game clock on EVERY mode —
  // an ad the player can't skip must not eat their completion time. The
  // ref mirror lets the running setInterval bail out even before React
  // commits (native AdMob overlay can cover the WebView first).
  const adOnScreen = hintAdOpen || shuffleAdOpen || undoAdOpen || continueAd;
  const adOnScreenRef = useRef(false);
  useEffect(() => { adOnScreenRef.current = adOnScreen; }, [adOnScreen]);
  const [startError, setStartError] = useState(null);
  const [muteState, setMuteState] = useState(isMuted());
  const [bursts, setBursts] = useState([]); // celebration bursts
  const [smashPairs, setSmashPairs] = useState([]); // endurance collide+shatter pairs
  const [levelCleared, setLevelCleared] = useState(false);
  const [dailyWorkout, setDailyWorkout] = useState(null);
  const [countdownActive, setCountdownActive] = useState(true); // 3-2-1-GO before play
  const [endConfirm, setEndConfirm] = useState(false); // confirm before ending
  const [timeUpDialog, setTimeUpDialog] = useState(false); // quick-mode timeout
  const [dwTimeUpDialog, setDwTimeUpDialog] = useState(false); // daily workout
  const [dwBonusForfeited, setDwBonusForfeited] = useState(false); // user chose continue without bonus
  const [paused, setPaused] = useState(false); // user-initiated pause
  const [firstMatchCelebration, setFirstMatchCelebration] = useState(0); // coin amount (0 = hidden)
  const [worldUnlock, setWorldUnlock] = useState(null); // {world_id,title,subtitle} or null
  const [enduranceBest, setEnduranceBest] = useState(null); // {timeSec,prevBest,mile,...} or null
  const [showPartnerPerk, setShowPartnerPerk] = useState(false); // occasional in-game reward popup

  // Occasionally surface the partner perk as a little post-win reward (~18%),
  // unless the player has opted out. Fires once per completed game (endDialog
  // is set once per game). Delayed so it lands after the win celebration.
  useEffect(() => {
    if (!endDialog?.won) return;
    if (!partnerPerksEnabled()) return; // UK-only affiliate — never for Polish
    if (partnerPerkOptedOut()) return;
    // QA hook: set localStorage 'fitjong:forcePerk'='1' to always show it on a
    // win (bypasses the 18% chance). Harmless for real users who never set it.
    let forced = false;
    try { forced = localStorage.getItem("fitjong:forcePerk") === "1"; } catch (_) {}
    if (!forced && Math.random() > 0.18) return;
    const id = setTimeout(() => setShowPartnerPerk(true), 1100);
    return () => clearTimeout(id);
  }, [endDialog]);
  const comboRef = useRef({ count: 0, last: 0 }); // rapid-match streak tracker
  const winFiredRef = useRef(false);
  const firstMatchTriedRef = useRef(false); // guards /match/first-of-day call (once per session)
  const boardRef = useRef(null);
  const startedRef = useRef(false);
  const finalizedRef = useRef(false); // guards against double-call to /game/complete
  // Mirror current tiles in a ref so async reward-ad callbacks (watchHintAd,
  // watchShuffleAd, watchContinueAd) read the LATEST state, not the snapshot
  // captured when MockAdDialog opened. This was making "Coach Tip" do
  // nothing after the ad dismissed because findHint() received a stale
  // tile array.
  const tilesRef = useRef([]);
  // Iter70 — selectedRef enables onTileClick to read `selected` without
  // listing it in the useCallback deps. Combined with reading tiles
  // from tilesRef, onTileClick gets a STABLE identity so React.memo on
  // Tile actually skips re-renders for tiles whose props didn't change.
  // Before: all 134 tiles re-rendered on every click. After: only 2-4.
  const selectedRef = useRef(null);
  // Iter80 — Refs for state read inside onTileClick. Putting these
  // in deps (as we did pre-iter80) re-created onTileClick whenever
  // any of them flipped, which propagated to all 134 React.memo
  // <Tile> children → every booster ad / profile refresh re-rendered
  // the entire board (visible as "lag on big boards"). Reading from
  // refs keeps onTileClick at a stable identity for the lifetime of
  // the session.
  const pausedRef = useRef(false);
  const timeUpDialogRef = useRef(false);
  const dwTimeUpDialogRef = useRef(false);
  const profileRef = useRef(null);

  const quick = worldId === "quick";
  // iter34 — three new run-types share the Play.jsx renderer but hit
  // different backend endpoints. Detected purely by the URL slug; nothing
  // else in the rendering loop changes.
  const endurance = worldId === "endurance";
  const daily = worldId === "daily";
  const weekly = worldId === "weekly";
  const specialMode = endurance || daily || weekly;
  // Iter79 — "Competitive" modes (Daily Workout, Weekly Tournament,
  // Endurance) score players by completion time and are eligible for
  // leaderboards. To keep them fair, the master timer KEEPS TICKING
  // during booster rewarded ads — otherwise a player could watch ads
  // for unlimited free time pauses + free Coach Tips and dominate the
  // leaderboard. Quick / Journey are untimed for ranking purposes so
  // they still pause during ads (respects the player's time).
  const competitive = endurance || daily || weekly;

  // Keep tilesRef synced so async callbacks always see latest state.
  useEffect(() => { tilesRef.current = tiles; }, [tiles]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  // Iter80 — Mirror runtime state into refs that onTileClick reads
  // from, so its useCallback identity never changes.
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { timeUpDialogRef.current = timeUpDialog; }, [timeUpDialog]);
  useEffect(() => { dwTimeUpDialogRef.current = dwTimeUpDialog; }, [dwTimeUpDialog]);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // Iter80 — Aggressive ad preload on Play mount. The first booster
  // ad of a session is the slowest (cold SDK + cold ad fetch). Calling
  // preloadRewarded() the moment the user opens Play warms the cache
  // so when they later tap "Watch ad" on the OOS dialog, the ad is
  // already prepped and shows in <1 s instead of 6-15 s. No-op on web.
  // Guarded via a ref so we only warm ONCE per Play mount even if the
  // profile loads asynchronously after mount.
  const preloadedRef = useRef(false);
  useEffect(() => {
    if (preloadedRef.current) return;
    if (!profile?.user_id) return;
    preloadedRef.current = true;
    preloadRewarded({ userId: profile.user_id, rewardType: "booster" }).catch(() => {});
  }, [profile?.user_id]);

  const startGame = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    finalizedRef.current = false;
    winFiredRef.current = false;
    const modeName = quick ? "quick" : daily ? "daily" : weekly ? "weekly" : endurance ? "endurance" : "journey";

    // Offline fallback — generate a guaranteed-solvable board locally and
    // start an OFFLINE session. Results are queued and synced on reconnect.
    const startOffline = async () => {
      const meta = await resolveOfflineBoard({ mode: modeName, worldId, levelId, quick });
      const tilesLocal = buildLayout(meta.layout);
      const offlineSid = `off_${modeName}_${Date.now()}`;
      const sess = {
        session_id: offlineSid,
        tiles: tilesLocal,
        layout: meta.layout,
        target_time_sec: meta.target_time_sec,
        offline: true,
        mode: specialMode ? worldId : modeName,
      };
      setSession(sess);
      setTiles(tilesLocal.map((t) => ({ ...t, removed: false })));
      // Optimistic local energy decrement (reconciled by /game/sync).
      setProfile((p) => (p ? { ...p, energy: Math.max(0, (p.energy || 0) - 1) } : p));
      toast(t("offline.playing", "Offline — your progress will sync when you're back online"), { duration: 4000 });
    };

    try {
      if (!isOnline()) { await startOffline(); return; }
      let endpoint = "/game/start";
      let payload = quick
        ? { world_id: "couch_to_active", level_id: "ca_1", quick: true }
        : { world_id: worldId, level_id: levelId, quick: false };
      if (endurance) { endpoint = "/endurance/start"; payload = {}; }
      else if (daily) { endpoint = "/daily-challenge/start"; payload = {}; }
      else if (weekly) { endpoint = "/weekly-tournament/start"; payload = {}; }

      const { data } = await api.post(endpoint, payload);
      // Special modes return {tiles, layout, ...} directly (no session
      // record); shape it into the structure the rest of Play.jsx expects.
      if (specialMode) {
        setSession({
          session_id: `${worldId}-${Date.now()}`,
          tiles: data.tiles,
          layout: data.layout,
          target_time_sec: data.target_time_sec,
          mode: worldId,
          mile_number: data.mile_number,
        });
        setTiles(data.tiles.map((t) => ({ ...t, removed: false })));
        setProfile(data.profile);
        cacheModeBoard(modeName, data.layout, data.target_time_sec);
      } else {
        setSession(data.session);
        setTiles(data.session.tiles.map((t) => ({ ...t, removed: false })));
        setProfile(data.profile);
        cacheModeBoard(modeName === "quick" ? "quick" : "journey", data.session.layout, data.session.target_time_sec);
      }
    } catch (e) {
      // No server response == network failure → play offline. A real HTTP
      // error (e.g. 400 out-of-energy) still surfaces as a start error.
      if (!e?.response) {
        try { await startOffline(); return; } catch (_) { /* fall through */ }
      }
      setStartError(e?.response?.data?.detail || "Could not start game");
    }
  }, [quick, worldId, levelId, setProfile, endurance, daily, weekly, specialMode, t]);

  useEffect(() => { startGame(); }, [startGame]);

  // Fetch Daily Workout status once per game — used to show the mini-challenge banner.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/daily-workout");
        setDailyWorkout(data);
      } catch {}
    })();
  }, [worldId, levelId]);

  const dwChallengeActive =
    dailyWorkout?.challenge
    && dailyWorkout.challenge.status === "active"
    && dailyWorkout.challenge.world_id === worldId
    && dailyWorkout.challenge.level_id === levelId;
  const dwTargetTime = dwChallengeActive ? dailyWorkout.challenge.target_time_sec : null;
  const [dwExtraTime, setDwExtraTime] = useState(0); // seconds added by ad/coins
  const dwEffectiveTarget = dwTargetTime != null ? dwTargetTime + dwExtraTime : null;
  const dwRemaining = dwEffectiveTarget != null ? Math.max(0, dwEffectiveTarget - elapsed) : null;

  // Timer — only ticks once the 3-2-1-GO countdown finishes and not paused.
  // Also frozen while the DW or quick-mode time-up dialog is open so the
  // bonus window extension granted by ad/coins isn't eaten up by elapsed
  // ticks during the dialog.
  // Iter64 — also freeze while any "Out of stock" booster purchase dialog
  // is showing. Tester complaint: "the DW timer pauses on adPaused when
  // MockAdDialog opens, but the countdown still runs while hintOutOfStock
  // and shuffleOutOfStock dialogs are visible."
  // Iter66 — also freeze while the ad MockDialog is mounted (hint/shuffle/
  // undo/continue) so the entire OOS → ad → reward flow is one paused
  // window, not just the few seconds the ad video is playing.
  useEffect(() => {
    if (!session || endDialog || countdownActive || paused) return;
    if (dwTimeUpDialog || timeUpDialog) return;
    // Iter114 — Pause the clock while an actual rewarded AD is on screen on
    // EVERY mode (user request: watching a 30–60s ad shouldn't cost you a
    // 30–60s penalty on Daily/Weekly/Endurance). The `continueAd` time-up
    // ad is included so all ad flows behave consistently.
    if (adOnScreen) return;
    // The out-of-stock DECISION dialog (and the generic adPaused it sets)
    // only pauses on non-competitive modes. On Daily/Weekly/Endurance the
    // clock keeps running while the player *decides*, so they can't stall
    // the leaderboard clock by simply leaving the dialog open.
    if (!competitive) {
      if (adPaused) return;
      if (hintOutOfStock || shuffleOutOfStock || undoOutOfStock) return;
    }
    const t = setInterval(() => {
      // Belt-and-braces ref guard. The native AdMob overlay can cover the
      // WebView before React tears down this interval — never tick while an
      // ad is actually on screen (all modes), or while paused on casual.
      if (adOnScreenRef.current) return;
      if (!competitive && adPausedRef.current) return;
      setElapsed((e) => e + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [session, endDialog, countdownActive, paused, adPaused,
      dwTimeUpDialog, timeUpDialog, adOnScreen,
      hintOutOfStock, shuffleOutOfStock, undoOutOfStock,
      hintAdOpen, shuffleAdOpen, undoAdOpen, continueAd, competitive]);

  // Iter78 — Defensive `adPaused` reset effect. The OOS cancel buttons
  // (hint-oos-cancel / shuffle-oos-cancel / undo-oos-cancel) directly
  // mutate the open-state via setXxxOutOfStock(false), which Radix
  // Dialog observes as a prop change and unmounts WITHOUT firing
  // `onOpenChange`. That left adPaused stuck at true after a user
  // tapped "Not now" and the game timer never resumed (QA iter78
  // BUG-2). Watch every "this run-mode is paused for ads" flag and
  // auto-clear adPaused whenever they're all false — this covers
  // cancel buttons, Escape key, back-gesture, outside-click, and any
  // future close path uniformly.
  useEffect(() => {
    const anyAdFlowActive =
      hintOutOfStock || shuffleOutOfStock || undoOutOfStock ||
      hintAdOpen || shuffleAdOpen || undoAdOpen || continueAd;
    if (!anyAdFlowActive && adPausedRef.current) {
      setAdPaused(false);
    }
  }, [hintOutOfStock, shuffleOutOfStock, undoOutOfStock,
      hintAdOpen, shuffleAdOpen, undoAdOpen, continueAd]);

  // Quick mode is UNTIMED — the clock counts up purely for the player's
  // self-tracking. There is NO game-ending time limit (tester clarified
  // v31: time-pressure belongs only to Daily Workout). Quick ends when
  // the player clears the board, taps End, or runs out of moves.

  // Daily-Workout time-up. When the bonus countdown reaches 0 we freeze the
  // board and offer the user three choices: watch ad / pay coins to extend
  // the bonus window, or forfeit the bonus and keep playing the level.
  // levelCleared guards against a "win at exactly 0:00" race.
  useEffect(() => {
    if (!dwChallengeActive || !session || endDialog || dwTimeUpDialog || dwBonusForfeited || levelCleared) return;
    if (dwRemaining === 0) {
      setDwTimeUpDialog(true);
      playCoachTimeUp();
      sfx.invalid();
    }
  }, [dwRemaining, dwChallengeActive, session, endDialog, dwTimeUpDialog, dwBonusForfeited, levelCleared]);
  // Detect win
  useEffect(() => {
    if (!session || endDialog || levelCleared) return;
    if (winFiredRef.current) return; // hard guard — coach voice can never play twice
    const remaining = tiles.filter((t) => !t.removed);
    if (remaining.length === 0 && tiles.length > 0) {
      winFiredRef.current = true;
      sfx.levelPop();
      setLevelCleared(true);
      // Coach voice fires a beat after the pop — feels like a real trainer
      // shouting after a finishing move. We deliberately DO NOT play sfx.win()
      // synth fanfare on top — coach voice + synth overlap was confusing the
      // user (two voices at once on daily-workout completion).
      setTimeout(() => playCoachWin(), 250);
      setTimeout(() => {
        finalizeGame(true);
      }, 1400);
    }
  }, [tiles, session, levelCleared]); // eslint-disable-line

  const finalizeGame = async (won) => {
    if (!session) return;
    if (finalizedRef.current) return; // hard guard: each session can only finalize once
    finalizedRef.current = true;
    // Compute reward fallback so the end dialog ALWAYS shows even if the
    // backend POST fails (offline, timeout, etc.). Without this, testers
    // got stuck on a "0 tiles, clock still ticking" screen with no win
    // celebration.
    const tilesMatchedFallback = tiles.filter((t) => t.removed).length / 2;
    const fallbackEnd = {
      won,
      coins: won ? Math.max(10, Math.round(tilesMatchedFallback * 2)) : 0,
      calories: Math.max(1, Math.round(tilesMatchedFallback / 2)),
      daily_workout_bonus: 0,
    };
    // --- Offline finalize: queue the result + optimistically patch the HUD.
    const modeName = quick ? "quick" : daily ? "daily" : weekly ? "weekly" : endurance ? "endurance" : "journey";
    const opWorld = quick ? "couch_to_active" : worldId;
    const opLevel = quick ? "ca_1" : levelId;
    const offlineFinalize = async () => {
      await enqueueOp({
        mode: modeName,
        worldId: opWorld,
        levelId: opLevel,
        won,
        timeTakenSec: elapsed,
        tilesMatched: tilesMatchedFallback,
        boostersUsed,
      });
      setProfile((p) => (p ? {
        ...p,
        coins: (p.coins || 0) + fallbackEnd.coins,
        calories_burned: (p.calories_burned || 0) + fallbackEnd.calories,
        total_games: (p.total_games || 0) + 1,
        total_wins: (p.total_wins || 0) + (won ? 1 : 0),
      } : p));
      setEndDialog({ ...fallbackEnd, offline: true });
      if (won) maybeRequestReview("win");
      // In case connectivity is actually back, try an immediate flush.
      flushQueue().then((r) => { if (r.ok && r.profile) setProfile(r.profile); }).catch(() => {});
    };
    // Session was started offline (locally generated board) → never hit the
    // network; queue immediately.
    if (session.offline || !isOnline()) { await offlineFinalize(); return; }
    try {
      let endpoint = "/game/complete";
      let body = {
        session_id: session.session_id,
        won,
        time_taken_sec: elapsed,
        tiles_matched: tilesMatchedFallback,
        boosters_used: boostersUsed,
      };
      if (endurance) {
        endpoint = "/endurance/complete";
        body = { won, time_sec: elapsed, tiles_matched: tilesMatchedFallback };
      } else if (daily) {
        endpoint = "/daily-challenge/complete";
        body = { won, time_sec: elapsed, tiles_matched: tilesMatchedFallback };
      } else if (weekly) {
        endpoint = "/weekly-tournament/complete";
        body = { won, time_sec: elapsed, tiles_matched: tilesMatchedFallback };
      }
      const { data } = await api.post(endpoint, body);
      setProfile(data.profile);
      // Interstitial every 3 games for non-premium (reduced from every 3
       // after tester complaint about ad frequency)
      const counter = data.profile.interstitial_counter || 0;
      if (!data.profile.no_ads && counter % 3 === 0) {
        if (isNative()) {
          // Fire real AdMob interstitial; no UI dialog needed.
          showInterstitial()
            .then(r => console.warn("[AD] interstitial result", r))
            .catch(e => console.warn("[AD] interstitial threw", e));
        } else {
          setInterstitial(true);
          setInterstitialSecs(4);
        }
      }
      setEndDialog({
        won: data.won,
        coins: data.coins_earned || data.coins_awarded || 0,
        calories: data.calories_earned || (won ? 10 : 2),
        daily_workout_bonus: data.daily_workout_bonus || 0,
        // Iter100 — next journey board for the "Continue" button.
        next_world: data.next_world || null,
        next_level: data.next_level || null,
        // Mode-specific extras surfaced by the end dialog so the user sees
        // "Mile 7 complete!" / "Daily streak 4 →" instead of generic copy.
        miles: data.miles,
        streak: data.streak,
        streak_advanced: data.streak_advanced,
        // Iter72 — Clean Win bonus (journey + quick modes).
        clean_win: !!data.clean_win,
        clean_win_bonus: data.clean_win_bonus || 0,
        // Iter73 — Clean Win streak progress chip.
        clean_win_streak: data.clean_win_streak || 0,
        clean_win_streak_bonus: data.clean_win_streak_bonus || 0,
        clean_win_streak_threshold: data.clean_win_streak_threshold || 5,
        // Iter117 — skill progression stats surfaced on Daily/Weekly wins.
        levels_gained: data.levels_gained || 0,
        game_score: data.game_score || 0,
        speed_tier: data.speed_tier || null,
        // Per-level personal best (journey mode) — drives the "New personal
        // best!" burst on the results screen when a replay beats the old time.
        personal_best: !!data.personal_best,
        prev_best_time_sec: data.prev_best_time_sec ?? null,
        best_time_sec: data.best_time_sec ?? null,
      });
      // Iter116 — a level clear opened a new world → one-time celebration.
      if (data.unlocked_world) setWorldUnlock(data.unlocked_world);
      // Iter127 — Endurance "New personal best!" celebration when the
      // player beats their fastest-ever clear.
      if (endurance && data.won && data.new_best) {
        setEnduranceBest({
          timeSec: elapsed,
          prevBest: data.prev_best_time_sec,
          mile: data.miles,
          coins: data.coins_awarded || 0,
          calories: data.calories_earned || 8,
          selectedAvatar: data.profile?.selected_avatar || null,
        });
      }
      // Iter104 — after a positive moment (a win, or a 5-day+ streak) ask
      // for a Google Play review. No-op on web; throttled client-side and
      // by Google. Fire-and-forget so it never blocks the end screen.
      if (data.won) {
        const streak = Number(data.streak || 0);
        maybeRequestReview(streak >= 5 ? "streak" : "win");
      }
      // Fire-and-forget step-sync — do NOT await. Awaiting it (iter45) put
      // the user's tap chain behind a potentially slow Health Connect read,
      // which on slow phones meant the player saw the end dialog freeze
      // for 5-15s after pressing Next. Tester complaint v37: "after I
      // finish a quick sesh everything freezes". Now the dialog appears
      // instantly and the step-sync result trickles in whenever it arrives.
      autoSyncStepsIfDue().then((r) => {
        if (r?.ok && (r.energy_granted || 0) > 0) {
          setProfile(r.profile);
        }
      }).catch(() => {});
    } catch (e) {
      // Backend save failed. If it's a NETWORK failure (no response) the
      // player is offline — queue the result so it syncs later. A real HTTP
      // error still shows the fallback dialog so the player isn't stuck.
      // eslint-disable-next-line no-console
      console.warn("game/complete failed", e);
      if (!e?.response) {
        await offlineFinalize();
      } else {
        toast(t("offline.saved", "Game saved offline — you'll sync next time you open the app"));
        setEndDialog(fallbackEnd);
      }
    }
  };

  // Interstitial countdown
  useEffect(() => {
    if (!interstitial) return;
    if (interstitialSecs <= 0) { setInterstitial(false); return; }
    const t = setTimeout(() => setInterstitialSecs((s) => s - 1), 900);
    return () => clearTimeout(t);
  }, [interstitial, interstitialSecs]);

  const autoShuffleRef = useRef(0);

  // Iter64 — Tester perf optimization: "Please memoize computeFreeSet,
  // run findHint only once per match in a useEffect, and only call
  // reseedSolvable when the free set is actually empty after the state
  // settles." The freeSet is the set of tile_ids currently selectable.
  // It's the cheap precursor to findHint — if no two free tiles share
  // a tile_type, findHint returns null and we go down the (expensive)
  // reseed path. Memoizing it stops gym_fortress's mid-game from
  // recomputing 134 tile selectability checks on every render.
  const freeSet = useMemo(() => computeFreeSet(tiles), [tiles]);
  // Iter79 — Perf: was doing 5 × `Math.min/max(...tiles.map(t => t.X))`
  // every render — 5 × O(n) array allocations + 5 × O(n) spread spreads
  // = ~670 ops on a 134-tile board per parent render. Single-pass
  // reducer is ~5× cheaper AND useMemo skips the work entirely on
  // renders where `tiles` identity is unchanged (selected / hinted
  // changes).
  const bounds = useMemo(() => {
    if (tiles.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0, maxZ: 0 };
    let mx = Infinity, MX = -Infinity, my = Infinity, MY = -Infinity, mz = 0;
    for (const tl of tiles) {
      if (tl.x < mx) mx = tl.x;
      if (tl.x > MX) MX = tl.x;
      if (tl.y < my) my = tl.y;
      if (tl.y > MY) MY = tl.y;
      if (tl.z > mz) mz = tl.z;
    }
    return { minX: mx, maxX: MX, minY: my, maxY: MY, maxZ: mz };
  }, [tiles]);
  const hasMove = useMemo(() => {
    if (freeSet.size < 2) return false;
    const seen = new Map();
    for (const tl of tiles) {
      if (tl.removed) continue;
      if (!freeSet.has(tl.tile_id)) continue;
      if (seen.has(tl.tile_type)) return true;
      seen.set(tl.tile_type, tl.tile_id);
    }
    return false;
  }, [tiles, freeSet]);

  // Iter63/64 — Tester demand: "I dont like this autoshuffle, still the
  // player can't clear the board themselves. Make sure the boards are
  // created where no tile is stuck on each other. CAn you write a code
  // which generate a board every single time making sure that no tiles
  // stuck on the top of each other and no shuffle is needed?"
  //
  // The backend now generates boards via a true FORWARD-SIMULATION
  // (every board has a proven clear sequence baked in — see
  // `_forward_sim_solvable` in game_logic.py). However, the player can
  // still create deadlock states by matching same-type tiles in the
  // wrong order (when tile types are reused across multiple pairs).
  //
  // Strategy when the board becomes structurally dead-ended:
  //   1) Try `reseedSolvable` — re-type remaining tiles to expose a
  //      fresh clear path.
  //   2) If reseed fails (truly dead structural state, e.g., two tiles
  //      vertically stacked at same x,y), SILENTLY UNDO the last match
  //      so the player rewinds to a state that's still clearable. Show
  //      a gentle toast — never the disruptive "Stuck? Reshuffle" modal.
  //   3) Only as a last resort (no history left to undo), open the
  //      no-moves dialog.
  useEffect(() => {
    if (!session || endDialog || noMoves) return;
    const remaining = tiles.filter((tl) => !tl.removed);
    if (remaining.length === 0) return; // win handled elsewhere

    // Cheap gate — if there's at least one selectable matching pair
    // (`hasMove` derived from the memoized freeSet) we have a move and
    // skip the expensive reseed/undo paths entirely. This is the common
    // case after most matches.
    if (hasMove) {
      autoShuffleRef.current = 0;
      return;
    }

    // No moves — confirm with the canonical `findHint` once before
    // committing to the expensive reseed (avoids a false-positive race
    // where `freeSet` is stale during a setTiles batch).
    if (findHint(tiles) !== null) {
      autoShuffleRef.current = 0;
      return;
    }

    // Step 1: try solvable reseed.
    const reseeded = reseedSolvable(tiles);
    if (reseeded && findHint(reseeded) !== null) {
      autoShuffleRef.current = 0;
      setTiles(reseeded);
      sfx.shuffle();
      toast(t("play.toast_auto_reseed",
        "Board re-balanced — you can still clear it."),
        { duration: 1800 });
      return;
    }
    // Step 2: try generic guaranteed-shuffle.
    const fallback = shuffleRemaining(tiles, 60);
    if (findHint(fallback) !== null) {
      autoShuffleRef.current = 0;
      setTiles(fallback);
      sfx.shuffle();
      toast(t("play.toast_auto_reseed",
        "Board re-balanced — you can still clear it."),
        { duration: 1800 });
      return;
    }
    // Step 3: SILENT AUTO-UNDO. Rewind matches one at a time until the
    // board has a valid move again. Only show the no-moves dialog if
    // we run out of history (extreme edge case on a freshly-shuffled
    // board with zero matches played).
    if (history.length > 0) {
      // Auto-undo by reusing the existing undo handler if available,
      // but do it inline so we don't accidentally consume a booster.
      const last = history[history.length - 1];
      setHistory((h) => h.slice(0, -1));
      setTiles((prev) => prev.map((tl) =>
        tl.tile_id === last.a || tl.tile_id === last.b
          ? { ...tl, removed: false }
          : tl
      ));
      sfx.shuffle();
      toast(t("play.toast_auto_rewind",
        "That match locked the board — last move rewound for you."),
        { duration: 2000 });
      return;
    }
    // Truly nothing playable from the very first move.
    autoShuffleRef.current = 0;
    sfx.fail();
    setNoMoves(true);
  }, [tiles, session, endDialog, noMoves, history, t, hasMove]);

  // Iter66/70 — useCallback with REF-based reads. tiles and selected
  // are read from `tilesRef.current` / `selectedRef.current` so this
  // callback keeps a STABLE identity across selections and matches.
  // Result: React.memo on Tile actually deduplicates renders — only
  // the 2-4 tiles whose own props changed re-render per click, not all
  // 134 (huge win on gym_fortress).
  const onTileClick = useCallback((tile, rect) => {
    // Iter80 — All conditional reads use refs so this callback keeps
    // a stable identity for the whole session (React.memo on <Tile>
    // skips re-renders for the 130+ unchanged tiles).
    if (timeUpDialogRef.current || dwTimeUpDialogRef.current ||
        pausedRef.current || adPausedRef.current) return; // board frozen
    if (tile.removed) return;
    const currentTiles = tilesRef.current;
    const currentSelected = selectedRef.current;
    if (!isSelectable(tile, currentTiles)) {
      // Audio-only feedback — testers said the toast hid the tiles they were
      // trying to read, and the "wrong" sound is enough.
      sfx.invalid();
      return;
    }
    setHint(null);
    if (!currentSelected) { sfx.select(); setSelected(tile.tile_id); return; }
    if (currentSelected === tile.tile_id) { sfx.tap(); setSelected(null); return; }
    const a = currentTiles.find((t) => t.tile_id === currentSelected);
    if (a && a.tile_type === tile.tile_type) {
      // match
      // Iter132 — Endurance match: the two matched tiles physically FLY
      // TOGETHER and collide (touch), then a light poof + a rock-crash sound
      // synced to the moment of impact. Capture the first tile's on-screen
      // rect NOW (before setTiles removes it) so the collide knows its origin.
      // Iter143 — Endurance now uses the SAME match feedback as every other
      // board (standard match sound + confetti MatchBurst). User asked to
      // drop the special fly-together/crash so all modes feel consistent.
      const smashOK = false;
      if (!smashOK) sfx.match();
      let aSmashRect = null;
      if (smashOK && boardRef.current) {
        const aEl = document.querySelector(`[data-testid="tile-${a.tile_id}"]`);
        if (aEl) aSmashRect = aEl.getBoundingClientRect();
      }
      // First matched pair of the day → server-idempotent +10 coin bonus.
      // Only fire once per page load and only if the profile hasn't already
      // claimed it today (avoids spamming the endpoint on every match).
      const todayKey = new Date().toISOString().slice(0, 10);
      const currentProfile = profileRef.current;
      if (
        !firstMatchTriedRef.current &&
        currentProfile?.first_match_today_date !== todayKey
      ) {
        firstMatchTriedRef.current = true;
        api.post("/match/first-of-day").then(({ data }) => {
          if (data?.granted) {
            setProfile(data.profile);
            sfx.coinCash?.();
            setFirstMatchCelebration(data.coins_awarded || 10);
          } else if (data?.profile) {
            setProfile(data.profile);
          }
        }).catch(() => {});
      }
      // Combo streak — if this match lands within 2.0s of the previous one,
      // increment the counter. The coach voice fires at thresholds of 3 / 5 / 7
      // for a satisfying arcade-style escalation, BUT we suppress the combo
      // line on the winning move so it doesn't overlap playCoachWin().
      const willClearBoard =
        currentTiles.filter((t) => !t.removed && t.tile_id !== a.tile_id && t.tile_id !== tile.tile_id).length === 0;
      const now = Date.now();
      if (now - comboRef.current.last <= 2000) {
        comboRef.current.count += 1;
      } else {
        comboRef.current.count = 1;
      }
      comboRef.current.last = now;
      if (!willClearBoard && [3, 5, 7].includes(comboRef.current.count)) {
        playCoachCombo(comboRef.current.count);
      }
      setHistory((h) => [...h, { a: a.tile_id, b: tile.tile_id }]);
      setTiles((prev) => prev.map((tl) =>
        tl.tile_id === a.tile_id || tl.tile_id === tile.tile_id
          ? { ...tl, removed: true }
          : tl
      ));
      setSelected(null);
      // spawn celebration burst at clicked tile rect
      if (rect && boardRef.current) {
        const parent = boardRef.current.getBoundingClientRect();
        const id = Math.random().toString(36).slice(2);
        const bx = rect.left - parent.left + rect.width / 2;
        const by = rect.top - parent.top + rect.height / 2;
        setBursts((b) => [...b, {
          id, x: bx, y: by,
          calories: 1 + Math.floor(Math.random() * 3),
        }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1100);
        // Iter132 — Endurance match FX: the two tiles fly together, TOUCH,
        // then a light poof + rock-crash sound synced to impact + a shake.
        if (smashOK) {
          const accent = TILE_META[tile.tile_type]?.c || "#EDC98A";
          const sp = {
            id: "c" + id,
            ax: aSmashRect ? aSmashRect.left - parent.left + aSmashRect.width / 2 : bx,
            ay: aSmashRect ? aSmashRect.top - parent.top + aSmashRect.height / 2 : by,
            bx, by,
            w: rect.width, h: rect.height,
            type: tile.tile_type,
            color: accent,
          };
          setSmashPairs((s) => [...s, sp]);
          setTimeout(() => setSmashPairs((s) => s.filter((p) => p.id !== sp.id)), 1050);
          // Stone-crush sound synced to the moment of IMPACT (rise ~180ms +
          // magnet snap ~180ms ≈ 360ms), so it lands exactly as they crash.
          setTimeout(() => sfx.smash(), 360);
        }
      }
    } else {
      sfx.invalid();
      setSelected(tile.tile_id);
    }
  }, []); // Iter80 — stable identity for whole session; state via refs.

  // Optimistically drop a booster count locally so the HUD updates instantly
  // and offline play consumes inventory correctly (no infinite use). When
  // online, the /boosters/use response overwrites this with the server truth.
  const decBoosterLocal = (key) =>
    setProfile((p) => (p ? { ...p, boosters: { ...(p.boosters || {}), [key]: Math.max(0, ((p.boosters?.[key]) ?? 0) - 1) } } : p));

  const useHint = async () => {
    if ((profile?.boosters?.hint ?? 0) <= 0) {
      setAdPaused(true);
      setHintOutOfStock(true);
      return;
    }
    const pair = findHint(tiles);
    if (!pair) { toast.error("No moves — try shuffle"); return; }
    // Highlight + count drop happen IMMEDIATELY — never gated on the network,
    // so it behaves identically online and offline.
    setHint(pair);
    sfx.hint();
    setBoostersUsed((b) => ({ ...b, hint: b.hint + 1 }));
    decBoosterLocal("hint");
    try {
      const { data } = await api.post("/boosters/use", { booster: "hint" });
      setProfile(data);
    } catch { /* offline — optimistic decrement stands; settles on sync */ }
    // Hint persists until the player taps any tile (cleared in onTileClick)
  };

  const watchHintAd = async () => {
    const pair = findHint(tilesRef.current);
    adDiag(`watchHintAd RAN (pair found = ${!!pair}, tiles = ${tilesRef.current?.length})`);
    if (pair) {
      setHint(pair);
      sfx.hint();
    } else {
      toast.error("No matching pair available right now", { duration: 3500 });
    }
    // Backend grant happens asynchronously — it just credits a +1
    // hint counter and updates the profile. No await before highlight.
    api.post("/boosters/reward-ad", { target: "hint" })
      .then(({ data }) => setProfile(data.profile))
      .catch(() => { /* the highlight already fired; backend retry not critical */ });
  };

  const buyHintWithCoins = async () => {
    try {
      const { data } = await api.post("/boosters/spend-coins", { booster: "hint" });
      setProfile(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not spend coins";
      toast.error(msg);
      return;
    }
    // Iter53 — was passing closure-captured `tiles`. If the board
    // state changed while the dialog was open (auto-shuffle, an
    // earlier match), findHint could return a stale pair where one
    // tile_id no longer matches a rendered tile → only ONE tile lit
    // up (tester report "Coins only show one tile"). Use the ref to
    // get the FRESH array, same as watchHintAd above.
    const pair = findHint(tilesRef.current);
    setHintOutOfStock(false);
    if (pair) {
      setHint(pair);
      sfx.hint();
    } else {
      toast.error("No matching pair available right now");
    }
  };

  // Clear pre-shuffle UI state (selected highlight + hint glow) so the
  // player can clearly see the new layout. Tester complaint v34: "when I
  // press Shuffle, it highlights a tile, why? I can't remember the
  // previous positions so I don't know if it actually shuffled."
  const resetShuffleUi = () => {
    setSelected(null);
    setHint(null);
  };

  const useShuffle = async () => {
    if ((profile?.boosters?.shuffle ?? 0) <= 0) {
      setAdPaused(true);
      setShuffleOutOfStock(true);
      return;
    }
    // Apply the effect + count drop immediately (works offline).
    resetShuffleUi();
    setTiles((prev) => shuffleRemaining(prev));
    setBoostersUsed((b) => ({ ...b, shuffle: b.shuffle + 1 }));
    setNoMoves(false);
    sfx.shuffle();
    toast.success("Warm-up reset! Tiles reshuffled");
    decBoosterLocal("shuffle");
    try {
      const { data } = await api.post("/boosters/use", { booster: "shuffle" });
      setProfile(data);
    } catch { /* offline — optimistic decrement stands; settles on sync */ }
  };

  const watchShuffleAd = async () => {
    try {
      const { data } = await api.post("/boosters/reward-ad", { target: "shuffle" });
      setProfile(data.profile);
    } catch {}
    resetShuffleUi();
    setTiles((prev) => shuffleRemaining(prev));
    setBoostersUsed((b) => ({ ...b, shuffle: b.shuffle + 1 }));
    setNoMoves(false);
    toast.success("Warm-up reset! Tiles reshuffled");
  };

  const buyShuffleWithCoins = async () => {
    try {
      const { data } = await api.post("/boosters/spend-coins", { booster: "shuffle" });
      setProfile(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not spend coins";
      toast.error(msg);
      return;
    }
    resetShuffleUi();
    setTiles((prev) => shuffleRemaining(prev));
    setBoostersUsed((b) => ({ ...b, shuffle: b.shuffle + 1 }));
    setNoMoves(false);
    setShuffleOutOfStock(false);
    sfx.shuffle();
    toast.success("−40 coins · Tiles reshuffled");
  };

  const useUndo = async () => {
    if (history.length === 0) { toast.error("Nothing to undo"); return; }
    if ((profile?.boosters?.undo ?? 0) <= 0) {
      setAdPaused(true);
      setUndoOutOfStock(true);
      return;
    }
    // Apply the effect + count drop immediately (works offline).
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setTiles((prev) => prev.map((t) => (t.tile_id === last.a || t.tile_id === last.b ? { ...t, removed: false } : t)));
    setBoostersUsed((b) => ({ ...b, undo: b.undo + 1 }));
    setNoMoves(false);
    sfx.undo();
    decBoosterLocal("undo");
    try {
      const { data } = await api.post("/boosters/use", { booster: "undo" });
      setProfile(data);
    } catch { /* offline — optimistic decrement stands; settles on sync */ }
  };

  const watchUndoAd = async () => {
    try {
      const { data } = await api.post("/boosters/reward-ad", { target: "undo" });
      setProfile(data.profile);
    } catch {}
    if (history.length === 0) {
      toast.success("Recovery Move granted — keep playing");
      return;
    }
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setTiles((prev) => prev.map((t) => (t.tile_id === last.a || t.tile_id === last.b ? { ...t, removed: false } : t)));
    setBoostersUsed((b) => ({ ...b, undo: b.undo + 1 }));
    setNoMoves(false);
    sfx.undo();
    toast.success("Recovery move used");
  };

  const buyUndoWithCoins = async () => {
    if (history.length === 0) {
      toast.error("Nothing to undo yet");
      return;
    }
    try {
      const { data } = await api.post("/boosters/spend-coins", { booster: "undo" });
      setProfile(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not spend coins";
      toast.error(msg);
      return;
    }
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setTiles((prev) => prev.map((t) => (t.tile_id === last.a || t.tile_id === last.b ? { ...t, removed: false } : t)));
    setBoostersUsed((b) => ({ ...b, undo: b.undo + 1 }));
    setUndoOutOfStock(false);
    sfx.undo();
    toast.success("−40 coins · Recovery used");
  };

  const watchContinueAd = async () => {
    try {
      const { data } = await api.post("/game/continue");
      setProfile(data);
      setTiles((prev) => shuffleRemaining(prev));
      setNoMoves(false);
      toast.success("Board reshuffled + boosters granted");
    } catch {
      toast.error("Could not continue");
    }
  };

  const giveUp = () => {
    // User explicitly chose to end the session. Show the end dialog
    // IMMEDIATELY with a fallback payload (in case the /game/complete POST
    // is slow / hanging) so the player is never stuck on a ticking board
    // wondering if their tap registered. finalizeGame() will overwrite
    // with authoritative server data when it returns.
    finalizedRef.current = false; // reset any stale guard from previous flow
    const tilesMatched = tiles.filter((t) => t.removed).length / 2;
    setEndDialog({
      won: false,
      coins: Math.max(0, Math.round(tilesMatched * 2)),
      calories: Math.max(0, Math.round(tilesMatched / 2)),
      daily_workout_bonus: 0,
    });
    finalizeGame(false);
  };

  if (startError) {
    const isEnergyError = /energy/i.test(startError);
    return (
      <div className="max-w-xl mx-auto text-center py-20">
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light mb-3">
          {isEnergyError ? t("play.out_of_energy_title", "Out of energy") : t("play.couldnt_start_title", "Couldn't start game")}
        </h1>
        <p className="text-[#2C332A]/70 mb-6">
          {/* Iter55 — was showing the raw backend HTTPException detail
              ("Out of energy. Refill or wait.") which stayed in English
              even when the app was localised in Polish. Use the i18n
              key for the energy case and only fall back to the raw
              detail for other errors. */}
          {isEnergyError ? t("play.out_of_energy_body", "Out of energy. Refill or wait.") : startError}
        </p>
        <div className="flex flex-col gap-3 items-center">
          {isEnergyError && (
            <Button
              data-testid="play-out-of-energy-refill-btn"
              onClick={() => navigate("/dashboard?refill=1")}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white px-8"
            >
              {t("dashboard.refill_energy")}
            </Button>
          )}
          <Button
            data-testid="play-back-to-dashboard-btn"
            variant={isEnergyError ? "outline" : "default"}
            onClick={() => navigate("/dashboard")}
            className={isEnergyError ? "rounded-full" : "rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"}
          >
            {t("play.back_to_dashboard", "Back to dashboard")}
          </Button>
        </div>
      </div>
    );
  }

  if (!session) return <p className="text-[#2C332A]/60">Preparing board…</p>;

  const { minX, maxX, minY, maxY, maxZ } = bounds;
  // Pad LEFT/TOP for the z-cascade (tiles at higher z drift up-right) and
  // RIGHT/BOTTOM for the shadow cascade — centres the whole board inside
  // the BoardCanvas' flex container.
  const padLeft = 12;
  const padRight = 12 + maxZ * LAYER_OFFSET_X;
  const padTop = 12 + maxZ * Math.abs(LAYER_OFFSET_Y);
  const padBottom = 12;
  const boardW = (maxX - minX + 1) * TILE_W + padLeft + padRight;
  const boardH = (maxY - minY + 1) * TILE_H + padTop + padBottom;

  const activeTheme = BOARD_THEMES[profile?.selected_theme] || BOARD_THEMES.zen_garden;
  const activeThemeId = profile?.selected_theme || "zen_garden";

  return (
    <div
      data-testid="play-page"
      className="safe-top safe-bottom relative px-2 md:px-8 pt-3 pb-3 w-screen flex flex-col overflow-hidden"
      style={{
        backgroundImage: activeTheme.page,
        color: "#F7F5F0",
        maxWidth: "100vw",
        // Lock page to dynamic viewport height so the DW banner + header +
        // board + boosters bar all share ONE screen — no vertical scroll
        // on Daily Workout (tester complaint v27: "Mini challenge -
        // screen not fit to the phone, scrollable up and down").
        // `dvh` accounts for Android browser chrome / system bars.
        height: "100dvh",
      }}
    >
      {/* Faint workout-silhouette watermark covering the whole page. */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage: `url('${activeTheme.photo}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "soft-light",
        }}
      />
      <div className="relative flex flex-col flex-1 max-w-xl w-full mx-auto gap-3 min-w-0">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <Button
          data-testid="play-exit-btn"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard")}
          className="rounded-full text-white/90 hover:bg-white/10 hover:text-white shrink-0 px-3 ml-1"
        >
          <Home size={16} strokeWidth={1.5} className="mr-1" /> {t("play.exit")}
        </Button>
        <div className="flex items-center gap-1.5 text-sm shrink-0">
          <div data-testid="play-timer" className="flex items-center gap-1 px-2.5 h-8 rounded-full bg-white/15 border border-white/20 text-white backdrop-blur-sm text-xs">
            <Clock size={12} strokeWidth={1.5} /> {formatTime(elapsed)}
          </div>
          <div data-testid="play-remaining" className="flex items-center gap-1 px-2.5 h-8 rounded-full bg-white/15 border border-white/20 text-white backdrop-blur-sm text-xs">
            <LayoutGrid size={12} strokeWidth={1.5} /> {tiles.filter((t) => !t.removed).length}
          </div>
          {/* Step counter was previously shown here. Removed per tester
             request v37: "Remove steps from the board view, re-add pause
             button. Numbers of steps synced not needed inside the
             board-game." */}
          <Button
            data-testid="play-pause-toggle"
            variant="ghost"
            size="sm"
            onClick={() => setPaused((p) => !p)}
            className="rounded-full h-8 w-8 p-0 bg-white/15 border border-white/20 text-white hover:bg-white/25 hover:text-white shrink-0"
            aria-label={paused ? t("play.resume_aria") : t("play.pause_aria")}
          >
            {paused
              ? <PlayIcon size={12} strokeWidth={1.5} />
              : <Pause size={12} strokeWidth={1.5} />}
          </Button>
          <Button
            data-testid="play-mute-toggle"
            variant="ghost"
            size="sm"
            onClick={() => { const n = !muteState; setMuted(n); setMuteState(n); }}
            className="rounded-full h-8 w-8 p-0 bg-white/15 border border-white/20 text-white hover:bg-white/25 hover:text-white shrink-0"
            aria-label={muteState ? t("play.unmute_aria") : t("play.mute_aria")}
          >
            {muteState
              ? <VolumeX size={12} strokeWidth={1.5} />
              : <Volume2 size={12} strokeWidth={1.5} />}
          </Button>
        </div>
      </div>

      {dwChallengeActive && (
        <div
          data-testid="daily-workout-banner"
          className="rounded-xl border border-[#E8A365]/40 px-3 py-1.5 flex items-center justify-between gap-2"
          style={{
            background: "linear-gradient(90deg, #FCD9A0 0%, #F6BA6B 100%)",
            color: "#4D3012",
          }}
        >
          {/* Iter74 — Compact one-line DW banner. Tester repeatedly
              reported "Daily Workout board is VERY VERY SMALL" — root
              cause was the old 2-row banner eating ~110 px of vertical
              space, forcing the board to scale down to fit the rest.
              Collapsed to single-row, lighter padding, smaller numerals
              → frees ~60 px for the board on phones. */}
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={14} strokeWidth={2} className="shrink-0" />
            <p className="text-xs font-medium truncate">
              {t("play.daily_workout_beat_in", {
                time: formatTime(dwTargetTime),
                coins: (dailyWorkout.challenge.base_reward_coins || 0) * (dailyWorkout.challenge.reward_multiplier || 2),
              })}
            </p>
          </div>
          <p className="text-base font-semibold tabular-nums shrink-0">{formatTime(dwRemaining)}</p>
        </div>
      )}

      {/* iter50 — added paddingBottom to leave space for the position:fixed
          booster bar below. Iter74 — tightened from 140 px to 96 px since
          the booster pill itself is ~80 px and safe-bottom adds another
          ~32 px. Saves ~44 px of vertical board space on every phone. */}
      <div
        className={`flex-1 flex justify-center min-h-0 ${dwChallengeActive ? "items-start" : "items-center"}`}
        style={{ paddingBottom: "calc(var(--safe-bottom, 24px) + 96px)" }}
      >
        <BoardCanvas
          tiles={tiles}
          freeSet={freeSet}
          selected={selected}
          hint={hint}
          onTileClick={onTileClick}
          minX={minX}
          minY={minY}
          boardW={boardW}
          boardH={boardH}
          padLeft={padLeft}
          padTop={padTop}
          bursts={bursts}
          smashPairs={smashPairs}
          boardRef={boardRef}
          theme={activeTheme}
          themeId={activeThemeId}
          dwActive={dwChallengeActive}
        />
      </div>

      {/* iter50 — POSITION-FIXED booster bar.
          Iter51 — uses var(--safe-bottom) (set by lib/safeArea.js from the
          native plugin) instead of env(safe-area-inset-bottom) because the
          Capacitor 7 Android WebView returns 0 for env() on edge-to-edge.
          Iter53 — tuning. Tester after iter51: "Coach Tip is now too high."
          Going from `48 + 80 = 128px` → `32 + 48 = 80px (web)` / `24-48 + 48`
          = `72-96px (Android)`. This puts the bar consistently ~80-96 px
          above the screen edge — comfortably clear of the gesture pill
          touch region but not floating awkwardly above empty board space. */}
      <div
        className="fixed left-0 right-0 z-30 px-2 md:px-8 pointer-events-none"
        data-testid="booster-bar"
        style={{
          bottom: "calc(var(--safe-bottom, 32px) + 48px)",
        }}
      >
        <div className="pointer-events-auto mx-auto max-w-xl w-full rounded-full bg-black/65 backdrop-blur-xl border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.55)] px-1.5 py-2 flex items-center justify-between gap-1.5">
          <BoosterBtn testid="booster-shuffle" onClick={useShuffle} icon={Shuffle} label={t("play.booster_warmup")} count={profile?.boosters?.shuffle ?? 0} />
          <BoosterBtn testid="booster-hint" onClick={useHint} icon={Lightbulb} label={t("play.booster_coach_tip")} count={profile?.boosters?.hint ?? 0} />
          <BoosterBtn testid="booster-undo" onClick={useUndo} icon={RotateCcw} label={t("play.booster_recovery")} count={profile?.boosters?.undo ?? 0} />
          <Button
            data-testid="booster-giveup"
            variant="ghost"
            size="sm"
            onClick={() => setEndConfirm(true)}
            className="rounded-full text-[#F6A4A4] hover:bg-[#D96C6C]/20 hover:text-[#F6A4A4] shrink-0 px-3"
          >
            {t("play.end_btn_short")}
          </Button>
        </div>
      </div>

      {/* No moves dialog — offers a free unlimited reshuffle so a rare
          unlucky 72-attempt auto-shuffle streak can't trap the user. End game
          remains as the escape hatch if they genuinely want to give up. */}
      <Dialog open={noMoves} onOpenChange={(o) => !o && setNoMoves(false)}>
        <DialogContent data-testid="no-moves-dialog" className="rounded-3xl border-[#E6E2D8] max-w-[330px] sm:max-w-[380px] p-5">
          <DialogHeader className="space-y-1.5">
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light">
              Stuck? Reshuffle the board
            </DialogTitle>
            <DialogDescription className="text-sm text-[#2C332A]/70 leading-snug">
              The remaining tiles can't form a pair right now. Shuffle them — it's free and won't cost a Warm-Up Reset.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <Button
              data-testid="no-moves-reshuffle"
              onClick={() => {
                const remaining = tiles.filter((t) => !t.removed);
                const next = shuffleRemaining(tiles, 60);
                setTiles(next);
                sfx.shuffle();
                setNoMoves(false);
                if (findHint(next) === null) {
                  // Genuinely structurally unwinnable. Mercy-win if the board
                  // is nearly cleared (≤6 tiles) — otherwise re-open dialog.
                  if (remaining.length <= 6) {
                    toast.success("Board cleared — well played!", { duration: 1800 });
                    finalizeGame(true);
                  } else {
                    setTimeout(() => setNoMoves(true), 50);
                    toast.error("Still stuck — try once more or end the game", { duration: 1600 });
                  }
                } else {
                  toast.success("Board reshuffled — keep going!");
                }
              }}
              className="rounded-full w-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-10 text-sm"
            >
              Shuffle again — free
            </Button>
            <Button
              data-testid="no-moves-endgame"
              variant="outline"
              onClick={() => { setNoMoves(false); finalizeGame(false); }}
              className="rounded-full w-full h-10 text-sm"
            >
              <XCircle size={14} strokeWidth={1.5} className="mr-2" /> {t("play.end_game")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Out-of-stock Warm-Up → offer ad or coins */}
      <Dialog
        open={shuffleOutOfStock}
        onOpenChange={(open) => {
          setShuffleOutOfStock(open);
          // Iter77 — clear the defensive adPaused if the user cancels.
          if (!open && !shuffleAdOpen) setAdPaused(false);
        }}
      >
        <DialogContent data-testid="shuffle-oos-dialog" className="rounded-3xl border-[#E6E2D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
              {t("play.shuffle_oos_title")}
            </DialogTitle>
            <DialogDescription>
              {t("play.shuffle_oos_desc")}
            </DialogDescription>
          </DialogHeader>
          {competitive && (
            <div
              data-testid="shuffle-oos-competitive-warning"
              className="text-xs text-[#B25E18] bg-[#FFF3E6] border border-[#F2D2A4] rounded-xl px-3 py-2 leading-snug"
            >
              {t("play.competitive_ad_warning")}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-3 flex-col sm:flex-row">
            <Button
              data-testid="shuffle-oos-cancel"
              variant="ghost"
              onClick={() => setShuffleOutOfStock(false)}
              className="rounded-full"
            >
              {t("play.not_now")}
            </Button>
            <Button
              data-testid="shuffle-oos-coins"
              variant="outline"
              onClick={buyShuffleWithCoins}
              className="rounded-full border-[#E6E2D8]"
            >
              <Coins size={14} strokeWidth={1.5} className="mr-2" /> {t("play.pay_coins", { n: 40 })}
            </Button>
            <Button
              data-testid="shuffle-oos-watch"
              onClick={() => { setShuffleOutOfStock(false); setAdPaused(true); setShuffleAdOpen(true); }}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <PlayCircle size={16} strokeWidth={1.5} className="mr-2" /> {t("daily_goal.watch_ad")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MockAdDialog
        open={shuffleAdOpen}
        onOpenChange={setShuffleAdOpen}
        title={t("play.shuffle_ad_title")}
        description={t("play.shuffle_ad_desc")}
        rewardLabel={t("play.shuffle_ad_reward")}
        rewardType="booster"
        onReward={watchShuffleAd}
        onWatchingChange={setAdPaused}
      />

      {/* Out-of-stock Coach Tip → offer ad or coins */}
      <Dialog
        open={hintOutOfStock}
        onOpenChange={(open) => {
          setHintOutOfStock(open);
          // Iter77 — when this dialog closes WITHOUT progressing to the
          // ad (user tapped "Not now" or back-gesture), resume the
          // timer that useHint paused defensively on entry.
          if (!open && !hintAdOpen) setAdPaused(false);
        }}
      >
        <DialogContent data-testid="hint-oos-dialog" className="rounded-3xl border-[#E6E2D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
              {t("play.hint_oos_title")}
            </DialogTitle>
            <DialogDescription>
              {t("play.hint_oos_desc")}
              <br />
              <span className="text-xs text-[#5F7161]/80 mt-1 inline-block">
                {t("play.your_balance", { coins: profile?.coins ?? 0 })}
              </span>
            </DialogDescription>
          </DialogHeader>
          {competitive && (
            <div
              data-testid="hint-oos-competitive-warning"
              className="text-xs text-[#B25E18] bg-[#FFF3E6] border border-[#F2D2A4] rounded-xl px-3 py-2 leading-snug"
            >
              {t("play.competitive_ad_warning")}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-3 flex-col sm:flex-row">
            <Button
              data-testid="hint-oos-cancel"
              variant="ghost"
              onClick={() => setHintOutOfStock(false)}
              className="rounded-full"
            >
              {t("play.not_now")}
            </Button>
            <Button
              data-testid="hint-oos-coins"
              variant="outline"
              onClick={buyHintWithCoins}
              disabled={(profile?.coins ?? 0) < 30}
              className="rounded-full border-[#E6E2D8]"
            >
              <Coins size={14} strokeWidth={1.5} className="mr-2" /> {t("play.pay_coins", { n: 30 })}
            </Button>
            <Button
              data-testid="hint-oos-watch"
              onClick={() => { setHintOutOfStock(false); setAdPaused(true); setHintAdOpen(true); }}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <PlayCircle size={16} strokeWidth={1.5} className="mr-2" /> {t("daily_goal.watch_ad")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MockAdDialog
        open={hintAdOpen}
        onOpenChange={setHintAdOpen}
        title={t("play.hint_ad_title")}
        description={t("play.hint_ad_desc")}
        rewardLabel={t("play.hint_ad_reward")}
        rewardType="booster"
        onReward={watchHintAd}
        onWatchingChange={setAdPaused}
      />

      {/* Out-of-stock Recovery → offer ad or coins */}
      <Dialog
        open={undoOutOfStock}
        onOpenChange={(open) => {
          setUndoOutOfStock(open);
          // Iter77 — clear the defensive adPaused if the user cancels.
          if (!open && !undoAdOpen) setAdPaused(false);
        }}
      >
        <DialogContent data-testid="undo-oos-dialog" className="rounded-3xl border-[#E6E2D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
              {t("play.undo_oos_title")}
            </DialogTitle>
            <DialogDescription>
              {t("play.undo_oos_desc")}
            </DialogDescription>
          </DialogHeader>
          {competitive && (
            <div
              data-testid="undo-oos-competitive-warning"
              className="text-xs text-[#B25E18] bg-[#FFF3E6] border border-[#F2D2A4] rounded-xl px-3 py-2 leading-snug"
            >
              {t("play.competitive_ad_warning")}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-3 flex-col sm:flex-row">
            <Button
              data-testid="undo-oos-cancel"
              variant="ghost"
              onClick={() => setUndoOutOfStock(false)}
              className="rounded-full"
            >
              {t("play.not_now")}
            </Button>
            <Button
              data-testid="undo-oos-coins"
              variant="outline"
              onClick={buyUndoWithCoins}
              className="rounded-full border-[#E6E2D8]"
            >
              <Coins size={14} strokeWidth={1.5} className="mr-2" /> {t("play.pay_coins", { n: 40 })}
            </Button>
            <Button
              data-testid="undo-oos-watch"
              onClick={() => { setUndoOutOfStock(false); setAdPaused(true); setUndoAdOpen(true); }}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <PlayCircle size={16} strokeWidth={1.5} className="mr-2" /> {t("daily_goal.watch_ad")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MockAdDialog
        open={undoAdOpen}
        onOpenChange={setUndoAdOpen}
        title={t("play.undo_ad_title")}
        description={t("play.undo_ad_desc")}
        rewardLabel={t("play.undo_ad_reward")}
        rewardType="booster"
        onReward={watchUndoAd}
        onWatchingChange={setAdPaused}
      />

      <MockAdDialog
        open={continueAd}
        onOpenChange={setContinueAd}
        title="Reshuffle + boost"
        description="Watch a short demo ad to reshuffle the board and gain +1 Coach Tip & +1 Warm-Up Reset."
        rewardLabel="Reward: Reshuffle + boosters"
        rewardType="continue"
        onReward={watchContinueAd}
        onWatchingChange={setAdPaused}
      />

      {/* Interstitial ad (web mock) */}
      <Dialog open={interstitial} onOpenChange={setInterstitial}>
        <DialogContent data-testid="interstitial-dialog" className="rounded-3xl border-[#E6E2D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
              Quick break · MOCKED ad
            </DialogTitle>
            <DialogDescription>
              This is a mocked interstitial ad. Continuing in {interstitialSecs}s…
            </DialogDescription>
          </DialogHeader>
          <div className="py-8 text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-[#8AA48F] mb-2">MOCKED AD</p>
            <p className="text-5xl font-light tabular-nums">{interstitialSecs}</p>
            <p className="text-xs text-[#2C332A]/50 mt-4">
              Pro Fitness Pack removes all interstitials.
            </p>
          </div>
          <DialogFooter>
            <Button
              data-testid="interstitial-skip"
              variant="outline"
              onClick={() => setInterstitial(false)}
              disabled={interstitialSecs > 0}
              className="rounded-full border-[#E6E2D8]"
            >
              {interstitialSecs > 0 ? t("play.interstitial_skip_in", { n: interstitialSecs }) : t("play.interstitial_continue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={dwTimeUpDialog} onOpenChange={() => {}}>
        <DialogContent
          data-testid="dw-timeup-dialog"
          className="rounded-3xl border-[#E6E2D8] [&>button]:hidden max-w-[330px] sm:max-w-[360px] p-5"
        >
          <DialogHeader className="space-y-1.5">
            <DialogTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-xl font-light flex items-center gap-2"
            >
              <Sparkles size={18} strokeWidth={1.5} className="text-[#E8A365]" />
              {t("play.daily_workout_timeup_title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#2C332A]/70 leading-snug">
              {t("play.daily_workout_timeup_desc", {
                coins: (dailyWorkout?.challenge?.base_reward_coins || 0) * (dailyWorkout?.challenge?.reward_multiplier || 2),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <Button
              data-testid="dw-extend-ad-btn"
              onClick={async () => {
                // Actually play a rewarded ad before granting +30s on native.
                // Also pause the game timer while the ad loads/plays so the
                // player doesn't lose seconds to ad load time.
                setAdPaused(true);
                try {
                  if (isNative()) {
                    const result = await showRewarded({ userId: profile?.user_id, rewardType: "dw_extend" });
                    if (!result?.rewarded) {
                      // Iter73 — Only surface the error toast on a true
                      // load/show failure. Closing the ad early (strict
                      // reward policy) returns rewarded:false without
                      // an error — the player simply doesn't get the
                      // bonus, no need to scold them with a toast.
                      if (result?.error) {
                        toast.error(t("play.ad_couldnt_load"));
                      }
                      return;
                    }
                  }
                  setDwTimeUpDialog(false);
                  setDwExtraTime((t) => t + 30);
                  toast.success(t("play.plus_30_seconds"));
                } finally {
                  setAdPaused(false);
                }
              }}
              className="rounded-full w-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-10 text-sm"
            >
              <PlayCircle size={14} strokeWidth={1.5} className="mr-2" />
              {t("play.watch_ad_plus_time")}
            </Button>
            <Button
              data-testid="dw-extend-coins-btn"
              variant="outline"
              disabled={(profile?.coins ?? 0) < 20}
              onClick={async () => {
                try {
                  await api.post("/store/spend_coins", { amount: 20, reason: "dw_extend_time" });
                  setDwTimeUpDialog(false);
                  setDwExtraTime((t) => t + 60);
                  await refresh();
                } catch (_) {
                  toast.error(t("play.couldnt_spend_coins"));
                }
              }}
              className="rounded-full w-full h-10 text-sm"
            >
              <Coins size={14} strokeWidth={1.5} className="mr-2" />
              {t("play.spend_20_plus_60s")}
            </Button>
            <button
              type="button"
              data-testid="dw-extend-exit-btn"
              onClick={() => {
                // User wants to bail. Forfeit the workout bonus and end the
                // session (giveUp shows the end dialog with current stats so
                // they don't lose the coins/calories they already earned).
                setDwTimeUpDialog(false);
                setDwBonusForfeited(true);
                giveUp();
              }}
              className="mt-1 text-xs text-[#2C332A]/60 hover:text-[#2C332A] underline underline-offset-2 py-1"
            >
              {t("play.exit_forfeit_bonus")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={timeUpDialog} onOpenChange={() => {}}>
        <DialogContent
          data-testid="timeup-dialog"
          className="rounded-3xl border-[#E6E2D8] [&>button]:hidden"
        >
          <DialogHeader>
            <DialogTitle
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-2xl font-light flex items-center gap-2"
            >
              <Clock size={20} strokeWidth={1.5} className="text-[#E8A365]" />
              {t("play.time_up_long_title")}
            </DialogTitle>
            <DialogDescription className="pt-2 text-[#2C332A]/70 leading-relaxed">
              {t("play.time_up_long_body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button
              data-testid="timeup-exit-btn"
              variant="outline"
              onClick={() => { finalizedRef.current = false; finalizeGame(false); navigate("/dashboard"); }}
              className="rounded-full w-full sm:w-auto"
            >
              <Home size={14} strokeWidth={1.5} className="mr-2" /> {t("play.exit")}
            </Button>
            <Button
              data-testid="timeup-retry-btn"
              onClick={() => {
                finalizedRef.current = false;
                setTimeUpDialog(false);
                setElapsed(0);
                setSelected(null);
                setHistory([]);
                comboRef.current = { count: 0, last: 0 };
                setLevelCleared(false);
                startedRef.current = false;
                setCountdownActive(true);
                startGame();
              }}
              className="rounded-full w-full sm:w-auto bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              <PlayCircle size={14} strokeWidth={1.5} className="mr-2" /> {t("play.try_again")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={endConfirm} onOpenChange={setEndConfirm}>
        <DialogContent data-testid="end-confirm-dialog" className="rounded-3xl border-[#E6E2D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
              {t("play.end_confirm_title")}
            </DialogTitle>
            <DialogDescription>
              {t("play.end_confirm_body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              data-testid="end-confirm-cancel"
              variant="outline"
              onClick={() => setEndConfirm(false)}
              className="rounded-full w-full sm:w-auto"
            >
              {t("play.keep_playing")}
            </Button>
            <Button
              data-testid="end-confirm-confirm"
              onClick={() => { setEndConfirm(false); giveUp(); }}
              className="rounded-full w-full sm:w-auto bg-[#D96C6C] hover:bg-[#C25555] text-white"
            >
              <XCircle size={14} strokeWidth={1.5} className="mr-2" /> {t("play.end_game")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!endDialog} onOpenChange={() => {}}>
        <DialogContent data-testid="end-dialog" className="rounded-3xl border-[#E6E2D8] [&>button]:hidden">
          <DialogHeader className="space-y-0.5">
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-semibold">
              {endDialog?.won ? t("play.well_done") : t("play.session_ended")}
            </DialogTitle>
            <DialogDescription className="text-[#2C332A]/60 text-sm">
              {endDialog?.won ? t("play.cleared_board") : t("play.progress_counts")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2.5 py-4 text-center">
            <StatChip icon={Clock} label={t("play.time")} value={formatTime(elapsed)} />
            <StatChip icon={Coins} label={t("play.coins")} value={`+${endDialog?.coins || 0}`} />
            <StatChip icon={Flame} label={t("play.kcal_short", "Kcal")} value={`+${endDialog?.calories || 0}`} />
          </div>
          {!daily && !weekly && !quick && !endurance && endDialog?.won && endDialog?.personal_best && (
            <motion.div
              data-testid="personal-best-chip"
              initial={{ opacity: 0, scale: 0.8, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 16 }}
              className="-mt-1 mb-2 px-3 py-2.5 rounded-xl flex items-center gap-2.5 text-white relative overflow-hidden"
              style={{ background: "linear-gradient(100deg,#E8A365 0%,#D9924E 55%,#C97F3C 100%)" }}
            >
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(110deg,transparent 30%,rgba(255,255,255,0.45) 50%,transparent 70%)" }}
                initial={{ x: "-120%" }}
                animate={{ x: "120%" }}
                transition={{ duration: 0.9, delay: 0.15, ease: "easeInOut" }}
              />
              <motion.div
                className="relative w-8 h-8 rounded-full bg-white/25 grid place-items-center shrink-0"
                animate={{ rotate: [0, -12, 12, 0] }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Trophy size={16} strokeWidth={2.2} className="text-white" />
              </motion.div>
              <div className="relative min-w-0 flex-1">
                <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-sm font-bold leading-tight">
                  {t("play.new_personal_best", "New personal best!")}
                </p>
                {endDialog?.prev_best_time_sec != null && (
                  <p className="text-[11px] text-white/90 leading-tight" data-testid="personal-best-delta">
                    {t("play.pb_beat_time", "Beat your old {{old}} by {{delta}}", {
                      old: formatTime(endDialog.prev_best_time_sec),
                      delta: formatTime(Math.max(0, endDialog.prev_best_time_sec - (endDialog.best_time_sec ?? elapsed))),
                    })}
                  </p>
                )}
              </div>
            </motion.div>
          )}
          {(daily || weekly) && endDialog?.won && endDialog?.game_score > 0 && (
            <div
              data-testid="skill-stats-chip"
              className="-mt-1 mb-2 px-3 py-2 rounded-xl border border-[#5F7161]/25 flex items-center justify-between gap-2"
              style={{ background: "linear-gradient(90deg,#EFF3EC 0%,#E3EADD 100%)", color: "#2C3D27" }}
            >
              <span className="flex items-center gap-1 text-[13px] font-semibold" data-testid="skill-stats-levels">
                <TrendingUp size={14} strokeWidth={2} className="text-[#5F7161]" />
                +{endDialog.levels_gained}
              </span>
              <span className="text-[13px] font-semibold text-[#5F7161]" data-testid="skill-stats-score">
                +{endDialog.game_score} {t("play.score", "score")}
              </span>
              {endDialog.speed_tier && (
                <span
                  data-testid="skill-stats-tier"
                  className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                  style={{
                    background: { gold: "#F0C36B", silver: "#CBD3DA", bronze: "#D19A6E", base: "#DCE1D6" }[endDialog.speed_tier] || "#DCE1D6",
                    color: "#3A2E12",
                  }}
                >
                  {t(`play.tier_${endDialog.speed_tier}`, endDialog.speed_tier)}
                </span>
              )}
            </div>
          )}
          {endDialog?.daily_workout_bonus > 0 && (
            <div
              data-testid="daily-workout-bonus-chip"
              className="-mt-1 mb-2 px-3 py-2 rounded-xl border border-[#E8A365]/40 flex items-center gap-2 text-[13px]"
              style={{ background: "linear-gradient(90deg, #FCD9A0 0%, #F6BA6B 100%)", color: "#4D3012" }}
            >
              <Sparkles size={14} strokeWidth={2} className="shrink-0" />
              <span className="font-semibold">{t("play.daily_workout_bonus")}</span>
              <span className="ml-auto font-bold">+{endDialog.daily_workout_bonus}</span>
            </div>
          )}
          {endDialog?.clean_win && endDialog?.clean_win_bonus > 0 && (
            <div
              data-testid="clean-win-chip"
              className="-mt-1 mb-2 px-3 py-2 rounded-xl border flex items-center gap-2 text-[13px]"
              style={{ background: "linear-gradient(90deg, #DCEFD4 0%, #B7DDA3 100%)", borderColor: "rgba(95, 113, 97, 0.35)", color: "#2C3D27" }}
            >
              <Sparkles size={14} strokeWidth={2} className="shrink-0" />
              <span className="font-semibold">{t("play.clean_win_short", "Clean win")}</span>
              <span className="font-bold text-[#3A6B2E]">+{endDialog.clean_win_bonus}</span>
              {endDialog.clean_win_streak >= endDialog.clean_win_streak_threshold ? (
                <span className="ml-auto flex items-center gap-0.5 font-bold text-[#B85C2E]" data-testid="clean-win-streak-flame">
                  <Flame size={13} strokeWidth={2} fill="#E8A365" className="text-[#E8A365]" />×{endDialog.clean_win_streak}
                </span>
              ) : (
                <span className="ml-auto text-xs font-bold text-[#5F7161] tabular-nums" data-testid="clean-win-streak-progress">
                  {endDialog.clean_win_streak}/{endDialog.clean_win_streak_threshold}
                </span>
              )}
            </div>
          )}
          {!daily && !weekly && !quick && !endurance && endDialog?.won && !endDialog?.next_world && (
            <div
              data-testid="journey-complete-endurance"
              className="-mt-1 mb-2 px-3 py-2 rounded-xl border flex items-center gap-2 text-[13px]"
              style={{ background: "linear-gradient(90deg,#DCEFD4 0%,#B7DDA3 100%)", borderColor: "rgba(95,113,97,0.35)", color: "#2C3D27" }}
            >
              <Trophy size={14} strokeWidth={2} className="shrink-0 text-[#B07B3C]" />
              <span className="font-medium">{t("play.journey_complete_short", "Journey complete! Endurance unlocked.")}</span>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {endDialog?.won && !(endDialog?.daily_workout_bonus > 0) && (
              <Button
                data-testid="end-dialog-share"
                onClick={() => shareResult({
                  title: "Fitjong",
                  text: buildShareText({
                    won: true,
                    timeSec: elapsed,
                    coins: endDialog?.coins || 0,
                    calories: endDialog?.calories || 0,
                    levelLabel: quick ? t("play.quick_mode") : translateLevelName(levelId, t, `${t("play.level")} ${levelId}`),
                  }),
                  card: {
                    won: true,
                    timeSec: elapsed,
                    coins: endDialog?.coins || 0,
                    calories: endDialog?.calories || 0,
                    // Iter40: pass already-translated world + level so the
                    // canvas never mixes EN/PL. Gender (for the PL
                    // "Rozwaliłem/Rozwaliłam" headline) is derived from
                    // the selected avatar inside renderShareCard.
                    worldTitle: quick ? "" : (translateWorldTitle({ world_id: worldId, title: worldId }, t) || ""),
                    levelName: quick ? t("play.quick_mode") : translateLevelName(levelId, t, levelId),
                    steps: Number(profile?.health?.last_steps || 0),
                    streak: Number(profile?.current_streak || 0),
                    selectedAvatar: profile?.selected_avatar || null,
                  },
                })}
                className="rounded-full bg-[#E8A365] hover:bg-[#D38F4F] text-white"
              >
                <Share2 size={14} strokeWidth={1.8} className="mr-2" /> {t("play.share_time")}
              </Button>
            )}
            {!(endDialog?.daily_workout_bonus > 0) && (
            <Button
              data-testid="end-dialog-context"
              variant="outline"
              className="rounded-full"
              onClick={() => {
                if (daily) navigate("/daily-challenge");
                else if (weekly) navigate("/weekly-tournament");
                else if (quick || endurance) navigate(0);
                else if (!endDialog?.won) navigate(0);
                else if (endDialog?.next_world && endDialog?.next_level) navigate("/journey", { state: { focus: { world_id: endDialog.next_world, level_id: endDialog.next_level } } });
                else navigate("/endurance");
              }}
            >
              {daily
                ? t("play.back_to_daily")
                : weekly
                ? t("play.back_to_weekly")
                : endurance
                ? t("play.next_board", "Next board")
                : quick
                ? t("play.play_again")
                : !endDialog?.won
                ? t("play.try_again")
                : (endDialog?.next_world && endDialog?.next_level)
                ? t("play.continue_next")
                : t("play.go_endurance")}
            </Button>
            )}
            <Button
              data-testid="end-dialog-home"
              onClick={() => navigate("/dashboard")}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              {t("play.home")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
      <WinCelebration active={levelCleared} />
      <WorldUnlockCelebration
        open={!!worldUnlock}
        world={worldUnlock}
        onDone={() => setWorldUnlock(null)}
      />

      <EnduranceBestCelebration
        open={!!enduranceBest}
        data={enduranceBest}
        onClose={() => setEnduranceBest(null)}
      />
      <PartnerPerkPopup open={showPartnerPerk} onClose={() => setShowPartnerPerk(false)} />
      <FirstMatchCelebration
        open={firstMatchCelebration > 0}
        coins={firstMatchCelebration}
        onDone={() => setFirstMatchCelebration(0)}
      />
      {countdownActive && session && (
        <CountdownOverlay onComplete={() => setCountdownActive(false)} />
      )}
      {paused && !countdownActive && !endDialog && !timeUpDialog && (
        <div
          data-testid="paused-overlay"
          onClick={() => setPaused(false)}
          className="fixed inset-0 z-[180] flex items-center justify-center cursor-pointer"
          style={{
            background: "radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="flex flex-col items-center gap-4 select-none">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "2px solid rgba(255,255,255,0.25)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
            >
              <Pause size={48} strokeWidth={1.5} className="text-white" />
            </div>
            <p
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-3xl font-light text-white tracking-wide"
            >
              {t("play.paused_title")}
            </p>
            <p className="text-sm text-white/70 tracking-wider uppercase">
              {t("play.tap_to_resume")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function BoosterBtn({ icon: Icon, label, onClick, testid, count }) {
  const out = count <= 0;
  return (
    <Button
      data-testid={testid}
      onClick={onClick}
      variant="ghost"
      size="sm"
      // Iter49 — tap target was previously ~38px tall (py-1.5), tester
      // reported hitting the Android system bar when reaching for Coach
      // Tip. Bumped vertical padding so the button is ~52px tall — clear
      // above Google's recommended 48dp minimum tap target.
      className="rounded-full flex flex-col h-auto py-3 px-2 gap-0.5 hover:bg-white/10 relative text-white/90 hover:text-white shrink min-w-0 flex-1"
    >
      <div className="flex items-center gap-1">
        <Icon size={16} strokeWidth={1.75} className={out ? "text-white/40 shrink-0" : "text-white shrink-0"} />
        <span
          data-testid={`${testid}-count`}
          className={`text-xs font-semibold tabular-nums px-1 min-w-[18px] text-center rounded-full ${
            out ? "bg-[#D96C6C]/25 text-[#F6A4A4]" : "bg-white/20 text-white"
          }`}
        >
          {count}
        </span>
      </div>
      <span className="text-[8px] uppercase tracking-tight mt-0.5 truncate max-w-full leading-tight">{label}</span>
    </Button>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div className="p-3 sm:p-4 rounded-2xl bg-[#F7F5F0] border border-[#E6E2D8] min-w-0">
      <Icon size={18} strokeWidth={1.5} className="text-[#5F7161] mx-auto mb-2" />
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[#8AA48F] truncate">{label}</p>
      <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-lg sm:text-xl font-light tabular-nums">{value}</p>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Auto-fitting board canvas — measures the wrapper width and scales the board
// so it always fits on-screen (portrait Samsung phone ≈ 360–412px wide).
const BoardCanvas = React.memo(function BoardCanvas({ tiles, freeSet, selected, hint, onTileClick, minX, minY, boardW, boardH, padLeft = 12, padTop = 12, bursts, smashPairs, boardRef, theme, themeId, dwActive = false }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Iter141 — BIGGER TILES. We size the board to fill the available space
  // based on its OWN initial footprint (the largest boardW/boardH seen for
  // this board), captured once. Tiles only get removed during play so the
  // board only shrinks — remembering the max keeps the scale rock-steady
  // (no mid-game zoom) while letting SPARSE boards render much larger tiles
  // (a 4-column board no longer scales as if it were 7 columns wide). The
  // ref resets naturally because BoardCanvas remounts on each new level.
  const maxDims = useRef({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const update = () => {
      // Iter56 — UNIFORM TILE SIZE GUARANTEE.
      // Every layout is bounded by REF_COLS × REF_ROWS, so scale is
      // computed ONLY against this reference grid. The per-board `fit`
      // override has been removed because it caused tiles to shrink
      // further on layouts that happened to occupy fewer rows — making
      // boards inconsistent. Now: same tile size everywhere.
      const parent = wrapRef.current?.parentElement;
      const REF_W = REF_COLS * TILE_W + 24 + 3 * LAYER_OFFSET_X;            // ≈ 608 px
      const REF_H = REF_ROWS * TILE_H + 24 + 3 * Math.abs(LAYER_OFFSET_Y);  // ≈ 1094 px
      // Iter68 — also subtract the outer div's own horizontal padding so
      // wide boards don't get clipped on the right edge of the screen.
      let outerHorizontalChrome = 24;
      if (wrapRef.current) {
        const cs = window.getComputedStyle(wrapRef.current);
        outerHorizontalChrome =
          (parseFloat(cs.paddingLeft) || 0) +
          (parseFloat(cs.paddingRight) || 0) +
          (parseFloat(cs.borderLeftWidth) || 0) +
          (parseFloat(cs.borderRightWidth) || 0);
      }
      const availW = parent
        ? Math.max(260, parent.clientWidth - 4 - outerHorizontalChrome)
        : Math.max(260, window.innerWidth - 16);

      let availH;
      const boosterEl = document.querySelector('[data-testid="booster-bar"]');
      // Iter68 — Tester report (production desktop): "Booster bar STILL
      // covering the bottom tiles" on bc_5 even with iter67's actualFit
      // clamp. Root cause: BoardCanvas's OUTER div has Tailwind
      // `p-1.5 md:p-5` padding (6 px mobile / 20 px desktop) + 1 px
      // border on each side. That padding sits OUTSIDE the scaled board
      // wrapper, so even when we sized the inner board to fit availH,
      // the outer div's bottom edge (board + padding + border) extended
      // past the booster bar — clipping the bottom row of tiles by
      // ~3 px on desktop. Read the wrapRef's actual padding+border via
      // getComputedStyle so the calc adapts to every breakpoint.
      let outerVerticalChrome = 24; // safe fallback
      if (wrapRef.current) {
        const cs = window.getComputedStyle(wrapRef.current);
        outerVerticalChrome =
          (parseFloat(cs.paddingTop) || 0) +
          (parseFloat(cs.paddingBottom) || 0) +
          (parseFloat(cs.borderTopWidth) || 0) +
          (parseFloat(cs.borderBottomWidth) || 0);
      }
      if (parent && boosterEl) {
        const parentRect = parent.getBoundingClientRect();
        const boosterRect = boosterEl.getBoundingClientRect();
        // 12 px breathing-room + the outer div's own padding+border so
        // the SCALED tiles inside never extend past the booster top.
        availH = Math.max(220, boosterRect.top - parentRect.top - 12 - outerVerticalChrome);
      } else if (parent) {
        availH = Math.max(220, parent.clientHeight - 4 - outerVerticalChrome);
      } else {
        availH = Math.max(220, window.innerHeight - 320);
      }

      // Iter64/65/66/67 — width-binding scale + adaptive fit floor.
      //
      // Tester report iter67 (production): "Booster bar STILL covering
      // the bottom tiles" on tall layouts. Screenshots show *journey
      // mode* boards (no DW banner) where the 10-row gym_fortress/
      // gym_temple overflows the available area because the iter65
      // scale used only the REF grid (7×7) and the actual 7×10 board
      // physically exceeded availH.
      //
      // New universal rule: scale is ALWAYS clamped by `actualFit`
      // (uses the real `boardW`/`boardH`) so the board can never extend
      // past the booster bar. The 44 px tile floor stays — but only
      // when the board can fit at that size. On viewports too short to
      // hold a 10-row board at 44 px, the tile shrinks below the floor
      // so the BOTTOM ROW REMAINS VISIBLE (the more important UX
      // guarantee per tester).
      // Iter141 — BIGGER TILES via per-board fill sizing.
      // Size to THIS board's own initial footprint (largest boardW/boardH
      // seen), not a fixed 7×9 reference grid. `actualFit` uses the real
      // board dims + a booster-aware `availH`, so the whole board always
      // fits and never slips under the booster bar; the CAP just stops a
      // tiny end-of-board layout from ballooning. Because we key off the
      // MAX dims (captured on first render, before any tile is cleared),
      // the scale stays constant for the whole game — no mid-play zoom.
      maxDims.current.w = Math.max(maxDims.current.w, boardW);
      maxDims.current.h = Math.max(maxDims.current.h, boardH);
      const fitW = availW / maxDims.current.w;
      const fitH = availH / maxDims.current.h;
      const CAP = 2.0; // hard ceiling — tiles never exceed ~160 px wide
      const s = Math.min(fitW, fitH, CAP);
      setScale(Number.isFinite(s) && s > 0 ? s : 1);
    };
    update();
    // Re-measure on next paint too — the booster bar is rendered AFTER
    // the BoardCanvas on first mount, so the first useLayoutEffect call
    // may not yet see it in the DOM.
    requestAnimationFrame(update);
    window.addEventListener("resize", update);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      if (wrapRef.current?.parentElement) ro.observe(wrapRef.current.parentElement);
      const boosterEl = document.querySelector('[data-testid="booster-bar"]');
      if (boosterEl) ro.observe(boosterEl);
    }
    return () => {
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
    };
  }, [boardW, boardH, dwActive]);

  const setRefs = (el) => {
    wrapRef.current = el;
    if (boardRef) boardRef.current = el;
  };

  return (
    <div
      ref={setRefs}
      data-testid={`board-theme-${themeId}`}
      className="rounded-3xl relative overflow-hidden border border-white/10 p-1.5 md:p-5"
      style={{
        backgroundImage: theme.board,
        boxShadow: "inset 0 0 140px rgba(0,0,0,0.55), 0 20px 40px -20px rgba(0,0,0,0.5)",
      }}
    >
      {/* gym athlete photo overlay - working out people */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.32]"
        style={{
          backgroundImage: `url('${theme.photo}')`,
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
          mixBlendMode: "soft-light",
        }}
      />
      {/* secondary gym-floor texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.22]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1600&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "overlay",
        }}
      />
      {/* vignette + subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.3) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.3) 0 1px, transparent 1px 40px)",
        }}
      />
      {/* soft spotlight */}
      <div
        className="absolute -inset-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,220,150,0.12) 0%, transparent 55%)",
        }}
      />
      <div
        className="relative mx-auto"
        style={{
          width: boardW * scale,
          height: boardH * scale,
        }}
      >
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            width: boardW,
            height: boardH,
            transform: `scale(${scale})`,
          }}
        >
        <BoardTiles
          tiles={tiles}
          freeSet={freeSet}
          selected={selected}
          hint={hint}
          minX={minX}
          minY={minY}
          padLeft={padLeft}
          padTop={padTop}
          onTileClick={onTileClick}
        />
        </div>
      </div>

      {/* Celebration bursts at match origins — confetti + "+N cal" floater.
          In Endurance the two matched tiles fly together, touch + poof. */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 50 }}>
        {(bursts || []).map((b) => (
          <MatchBurst key={b.id} x={b.x} y={b.y} calories={b.calories} />
        ))}
        {(smashPairs || []).map((p) => (
          <TileCollide key={p.id} pair={p} />
        ))}
      </div>
    </div>
  );
});

// Renders the live tiles using a memoized per-tile component so that
// clicking a single tile no longer re-renders the entire board. The
// `freeSet` (selectable tile ids) is computed ONCE in the parent
// `<Play>` component and threaded down as a prop — Iter66 dedupe: prior
// to this, `computeFreeSet(tiles)` ran twice per `tiles` change (once
// in Play.jsx, once here).
const BoardTiles = React.memo(function BoardTiles({ tiles, freeSet, selected, hint, minX, minY, padLeft, padTop, onTileClick }) {
  const sorted = React.useMemo(
    () =>
      tiles
        .filter((t) => !t.removed)
        .sort((a, b) => a.z - b.z || a.y - b.y || a.x - b.x),
    [tiles],
  );
  // Big-board performance mode: blurred box-shadows + spring physics are the
  // dominant mobile cost and it scales with tile count — so past a threshold
  // we switch to cheap solid-offset shadows and lightweight tween transitions.
  // Iter114 — lowered 90 → 64 so the heavy "fortress"/"temple" boards (and
  // any 70–90 tile board that still stuttered) get the cheap path too.
  const lite = sorted.length > 64;
  return (
    <AnimatePresence>
      {sorted.map((t) => (
        <Tile
          key={t.tile_id}
          tile={t}
          free={freeSet.has(t.tile_id)}
          selected={selected === t.tile_id}
          hinted={!!(hint && (hint[0] === t.tile_id || hint[1] === t.tile_id))}
          minX={minX}
          minY={minY}
          padLeft={padLeft}
          padTop={padTop}
          lite={lite}
          onTileClick={onTileClick}
        />
      ))}
    </AnimatePresence>
  );
});

const Tile = React.memo(function Tile({ tile: t, free, selected, hinted, minX, minY, padLeft, padTop, lite, onTileClick }) {
  const isSelected = selected;
  const isHinted = hinted;
  const left = (t.x - minX) * TILE_W + t.z * LAYER_OFFSET_X + padLeft;
  const top = (t.y - minY) * TILE_H + t.z * LAYER_OFFSET_Y + padTop;
  const meta = TILE_META[t.tile_type];
  const accent = meta?.c || "#5F7161";
  const sideHex = isSelected
    ? shade(accent, -30)
    : isHinted
    ? "#B25E18"
    : "#4E3B82";
  const stepCascade = lite
    ? [
        // Big-board LITE mode: solid offsets only (0 blur) → cheap to paint.
        `1px 1px 0 ${shade(sideHex, 30)}`,
        `2px 2px 0 ${shade(sideHex, 10)}`,
        `3px 3px 0 ${sideHex}`,
        `4px 4px 0 ${shade(sideHex, -12)}`,
      ].join(", ")
    : [
        `1px 1px 0 ${shade(sideHex, 35)}`,
        `2px 2px 0 ${shade(sideHex, 25)}`,
        `3px 3px 0 ${shade(sideHex, 15)}`,
        `4px 4px 0 ${shade(sideHex, 5)}`,
        `5px 5px 0 ${sideHex}`,
        `6px 6px 0 ${shade(sideHex, -8)}`,
        `7px 7px 0 ${shade(sideHex, -15)}`,
        `8px 8px 0 ${shade(sideHex, -22)}`,
        `9px 12px 18px rgba(0,0,0,0.45)`,
      ].join(", ");
  // Iter66 — Tester perf: "The staggered delay in the tile spring
  // transition applies on every animate change, not just on initial
  // mount. Restrict the delay to the initial mount animation so re-
  // selects and hint highlights are instant."
  // Solution: gate the stagger via a one-shot ref so the spring delay
  // is computed only for the FIRST mount of each tile. Subsequent
  // animations (select / hint pulse / scale-up on hover) run with
  // delay=0 → no perceptible lag.
  const mountedRef = useRef(false);
  const mountCap = lite ? 0.18 : 0.42;
  const mountDelay = mountedRef.current
    ? 0
    : Math.min(mountCap, t.z * 0.05 + ((t.y - minY) * 0.008) + ((t.x - minX) * 0.005));
  useEffect(() => {
    mountedRef.current = true;
  }, []);
  return (
    <motion.button
      data-testid={`tile-${t.tile_id}`}
      onClick={(e) => onTileClick(t, e.currentTarget.getBoundingClientRect())}
      initial={{ scale: 0.4, opacity: 0, y: -40 }}
      animate={{
        scale: isSelected ? 1.08 : isHinted ? 1.04 : 1,
        opacity: 1,
        y: isSelected ? -6 : 0,
      }}
      exit={{
        scale: 1.5,
        opacity: 0,
        rotate: Math.random() * 24 - 12,
        // Iter76 — Tester complaint "still lag on bigger boards when
        // removing tiles, feels like slow motion". Halved exit duration
        // from 0.34 s → 0.20 s; combined with the O(n²) → O(n) fix in
        // `computeFreeSet` this makes matches feel instant on every
        // board including gym_temple (134 tiles).
        transition: { duration: 0.20, ease: "easeOut" },
      }}
      whileHover={!lite && free ? { y: -4, scale: 1.04 } : {}}
      whileTap={!lite && free ? { scale: 0.95 } : {}}
      transition={
        lite
          ? { duration: 0.14, ease: "easeOut", delay: mountDelay }
          : { type: "spring", stiffness: 240, damping: 20, delay: mountDelay }
      }
      className={`absolute rounded-[10px] overflow-visible flex items-center justify-center ${
        free ? "cursor-pointer" : "cursor-not-allowed"
      }`}
      style={{
        left, top,
        width: TILE_W, height: TILE_H,
        zIndex: t.z * 100 + (t.y - minY) * 10 + (isHinted ? 5000 : 0) + (isSelected ? 6000 : 0),
        background: isSelected
          ? `linear-gradient(165deg, ${shade(accent, 20)} 0%, ${accent} 50%, ${shade(accent, -20)} 100%)`
          : isHinted
          ? "linear-gradient(165deg, #FFF4D8 0%, #FCD9A0 55%, #F5B872 100%)"
          : free
          ? "linear-gradient(165deg, #ffffff 0%, #fbf6e8 55%, #efe8d0 100%)"
          : "linear-gradient(165deg, #9C9484 0%, #847C6B 55%, #6B6453 100%)",
        border: isHinted ? "2px solid #D17A2A" : "1.5px solid rgba(0,0,0,0.08)",
        boxShadow: stepCascade + (
          lite
            ? ""
            : isSelected
            ? ", inset 0 2px 0 rgba(255,255,255,0.35), inset 0 -3px 5px rgba(0,0,0,0.25)"
            : free
            ? ", inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -3px 5px rgba(0,0,0,0.08), inset 2px 0 2px rgba(255,255,255,0.5)"
            : ", inset 0 0 0 1px rgba(0,0,0,0.25), inset 0 -3px 5px rgba(0,0,0,0.35)"
        ),
        opacity: free || isSelected || isHinted ? 1 : 0.7,
        padding: 0,
      }}
    >
      <span
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: "38%",
          borderTopLeftRadius: 9,
          borderTopRightRadius: 9,
          background: isSelected
            ? "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0) 100%)",
        }}
      />
      <TileFace
        type={t.tile_type}
        dim={!free && !isSelected}
        invert={isSelected}
        highlight={false}
        size={38}
      />
      {isHinted && (
        <motion.span
          className="absolute inset-0 rounded-[10px] pointer-events-none"
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(232,163,101,0.95)",
              "0 0 0 14px rgba(232,163,101,0)",
            ],
          }}
          transition={{ duration: 1.1, repeat: Infinity }}
        />
      )}
    </motion.button>
  );
});

// Small confetti-like burst + floating "+cal" number
function MatchBurst({ x, y, calories }) {
  const particles = React.useMemo(() => {
    const colors = ["#E8A365", "#D96C6C", "#5F7161", "#FCD9A0", "#5B9BD5"];
    return Array.from({ length: 10 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 10 + Math.random() * 0.4;
      const dist = 30 + Math.random() * 30;
      return {
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        color: colors[i % colors.length],
        rot: Math.random() * 360,
        size: 5 + Math.random() * 4,
      };
    });
  }, []);

  return (
    <>
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
          animate={{ opacity: 0, x: p.dx, y: p.dy - 10, scale: 0.4, rotate: p.rot }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="absolute block rounded-sm"
          style={{
            left: x, top: y,
            width: p.size, height: p.size,
            backgroundColor: p.color,
            boxShadow: `0 0 6px ${p.color}`,
            marginLeft: -p.size / 2,
            marginTop: -p.size / 2,
          }}
        />
      ))}
      <motion.div
        initial={{ opacity: 0, y: 0, scale: 0.6 }}
        animate={{ opacity: [0, 1, 1, 0], y: -48, scale: 1.1 }}
        transition={{ duration: 1.0, ease: "easeOut", times: [0, 0.15, 0.7, 1] }}
        className="absolute flex items-center gap-1 text-[#E8A365] font-semibold text-sm"
        style={{ left: x, top: y, transform: "translate(-50%, -50%)", textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}
      >
        <Flame size={14} strokeWidth={2.5} />
        +{calories} cal
      </motion.div>
    </>
  );
}

// Endurance match: the two matched tiles (1) RISE up for a split second,
// (2) get DRAGGED toward each other like magnets (accelerating), then
// (3) BOOM — a light poof at the contact point. No board shake.
function TileCollide({ pair }) {
  const midX = (pair.ax + pair.bx) / 2;
  const midY = (pair.ay + pair.by) / 2;
  const dx = pair.bx - pair.ax;
  const dy = pair.by - pair.ay;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  const half = pair.w * 0.5;
  const aTx = midX - ux * half, aTy = midY - uy * half;
  const bTx = midX + ux * half, bTy = midY + uy * half;
  // Three-beat impact per user spec: RISE (lift + glow) -> MAGNET (snap
  // toward each other until they TOUCH edge-to-edge) -> CRASH (violent
  // squash + rock debris shatter). Rock-crush sound fires at ~360ms.
  const [phase, setPhase] = React.useState("rise");
  React.useEffect(() => {
    const t1 = setTimeout(() => setPhase("magnet"), 180);
    const t2 = setTimeout(() => setPhase("crash"), 360);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  const ghost = (sx, sy, tx, ty, key) => {
    let anim, dur, ease;
    if (phase === "rise") {
      // Clearly LIFT the selected pair up and enlarge with a bright glow.
      anim = { x: sx, y: sy - 20, scale: 1.28, rotate: key === "a" ? -6 : 6, opacity: 1 };
      dur = 0.18; ease = "easeOut";
    } else if (phase === "magnet") {
      // Accelerate toward each other like magnets, stopping edge-to-edge
      // so the two tiles visibly TOUCH.
      anim = { x: tx, y: ty, scale: 1.12, rotate: 0, opacity: 1 };
      dur = 0.19; ease = [0.7, 0, 1, 1];
    } else {
      // CRASH — quick squash on impact then shatter/vanish.
      anim = { x: tx, y: ty, scale: [1.2, 0.92, 0.32], rotate: key === "a" ? -10 : 10, opacity: [1, 1, 0] };
      dur = 0.22; ease = "easeIn";
    }
    return (
      <motion.div
        key={key}
        initial={{ x: sx, y: sy, scale: 1, opacity: 1 }}
        animate={anim}
        transition={{ duration: dur, ease }}
        className="absolute rounded-[10px] overflow-hidden flex items-center justify-center pointer-events-none"
        style={{
          left: 0, top: 0,
          width: pair.w, height: pair.h,
          marginLeft: -pair.w / 2, marginTop: -pair.h / 2,
          background: "linear-gradient(165deg,#ffffff 0%,#fbf6e8 55%,#efe8d0 100%)",
          border: "1.5px solid rgba(0,0,0,0.08)",
          boxShadow: phase === "rise"
            ? `0 16px 26px rgba(0,0,0,0.45), 0 0 22px 4px ${pair.color}`
            : "0 5px 12px rgba(0,0,0,0.34)",
        }}
      >
        <TileFace type={pair.type} size={Math.max(20, Math.round(pair.w * 0.46))} />
      </motion.div>
    );
  };
  return (
    <>
      {ghost(pair.ax, pair.ay, aTx, aTy, "a")}
      {ghost(pair.bx, pair.by, bTx, bTy, "b")}
      {phase === "crash" && <CrashBurst x={midX} y={midY} w={pair.w} h={pair.h} color={pair.color} />}
    </>
  );
}

// Endurance CRASH impact — matches the user's reference clip: the tiles
// SHATTER into a dense cloud of small WHITE sparkling particles that burst
// outward radially and fade quickly (~0.6s). A brief soft white flash sits
// under the sparkle cloud. No grey rubble, no shockwave ring.
function CrashBurst({ x, y, w = 60, h = 78, color = "#EDC98A" }) {
  const sparks = React.useMemo(() => {
    return Array.from({ length: 26 }, (_, i) => {
      const a = (Math.PI * 2 * i) / 26 + (Math.random() - 0.5) * 0.6;
      const d = 14 + Math.random() * (w * 0.9);
      return {
        id: i,
        dx: Math.cos(a) * d,
        dy: Math.sin(a) * d + (Math.random() - 0.5) * 10,
        s: 2 + Math.random() * 4.5,
        delay: Math.random() * 0.05,
        dur: 0.4 + Math.random() * 0.3,
      };
    });
  }, [w]);
  return (
    <>
      {/* brief soft white flash */}
      <motion.span
        initial={{ opacity: 0.9, scale: 0.4 }}
        animate={{ opacity: 0, scale: 1.4 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
        className="absolute rounded-full pointer-events-none"
        style={{
          left: x, top: y, width: w, height: w,
          marginLeft: -w / 2, marginTop: -w / 2,
          background: "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.5) 45%, rgba(255,255,255,0) 72%)",
        }}
      />
      {/* dense white sparkle shatter */}
      {sparks.map((m) => (
        <motion.span
          key={m.id}
          initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
          animate={{ opacity: 0, x: m.dx, y: m.dy, scale: 0.2 }}
          transition={{ duration: m.dur, delay: m.delay, ease: "easeOut" }}
          className="absolute pointer-events-none"
          style={{
            left: x, top: y, width: m.s, height: m.s,
            marginLeft: -m.s / 2, marginTop: -m.s / 2,
            background: "#ffffff",
            borderRadius: "50%",
            boxShadow: "0 0 6px 1px rgba(255,255,255,0.9)",
          }}
        />
      ))}
    </>
  );
}

// Utility — shade a hex by a percent (-100..100)
function shade(hex, percent) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  let r = (n >> 16) & 0xff;
  let g = (n >> 8) & 0xff;
  let b = n & 0xff;
  const p = percent / 100;
  r = Math.round(Math.max(0, Math.min(255, r + (p < 0 ? r * p : (255 - r) * p))));
  g = Math.round(Math.max(0, Math.min(255, g + (p < 0 ? g * p : (255 - g) * p))));
  b = Math.round(Math.max(0, Math.min(255, b + (p < 0 ? b * p : (255 - b) * p))));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
